import React, { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  closestCenter,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import FacturasCompra from './FacturasCompra';
import {
  FileText,
  TrendingUp,
  Package,
  CheckCircle,
  Receipt,
  Clock,
  AlertTriangle,
  Calendar,
  DollarSign,
  Truck,
  RefreshCw,
  Plus,
  Search,
  XCircle,
  Eye,
  Edit,
  Send,
  ShoppingCart,
  Phone,
  MapPin,
  Mail,
  Loader2,
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { useCompras } from '../hooks/useCompras';
import { useProveedores } from '../hooks/useProveedores';
import {
  useOrdenesCompra,
  useCrearOrdenCompra,
  useEnviarOrdenCompra,
  useCancelarOrdenCompra,
  ordenesCompraKeys,
  // useRecepcionarOrdenCompra, // Para uso futuro
} from '../hooks/useOrdenesCompra';
import { OrdenCompra } from '../types/ordenes';
import WizardOrdenCompra from '../components/WizardOrdenCompra';
import DetalleOrdenCompra from '../components/DetalleOrdenCompra';
import EditarOrdenCompra from '../components/EditarOrdenCompra';
import RecepcionOrdenCompra from '../components/RecepcionOrdenCompra';
import {
  formatearFechaCorta,
  formatearFechaCon,
  obtenerFechaActualLocal,
  fechaLocalConOffset,
} from '../utils/dateUtils';

const Compras: React.FC = () => {
  const queryClient = useQueryClient();
  const { token, user } = useAuth();
  const [vistaActiva, setVistaActiva] = useState<
    'sugerencias' | 'ordenes' | 'historial' | 'facturas'
  >('sugerencias');

  // Estados para el manejo de órdenes de compra
  const [filtros, setFiltros] = useState({
    estado: '',
    proveedor: '',
    busqueda: '',
  });

  // Estados para paginación de órdenes
  const [paginaActual, setPaginaActual] = useState(1);
  const [elementosPorPagina] = useState(10);

  const [showFormulario, setShowFormulario] = useState(false);
  const [showDetalles, setShowDetalles] = useState(false);
  const [ordenSeleccionadaId, setOrdenSeleccionadaId] = useState<string>('');
  const [showEditar, setShowEditar] = useState(false);
  const [ordenEditarId, setOrdenEditarId] = useState<string>('');
  const [_showConfirmarEnvio, _setShowConfirmarEnvio] = useState(false);
  const [_ordenEnviarId, _setOrdenEnviarId] = useState<string>('');
  const [_ordenEnviarData, _setOrdenEnviarData] = useState<any>(null);
  const [showRecepcion, setShowRecepcion] = useState(false);
  const [ordenRecepcionId, setOrdenRecepcionId] = useState<string>('');

  // Estados para edición de sugerencias
  const [sugerenciasEditables, setSugerenciasEditables] = useState<any>(null);
  const [ingredientesSeleccionados, setIngredientesSeleccionados] = useState<
    Set<string>
  >(new Set());
  const [fechaEntregaDeseada, setFechaEntregaDeseada] = useState<string>('');

  // Estados para el modal de cambio de proveedor
  const [modalCambioProveedor, setModalCambioProveedor] = useState<{
    abierto: boolean;
    proveedorActual: string;
    ingredienteId: number;
    nombreIngrediente: string;
    categoriaIngrediente?: string;
  } | null>(null);
  const [recargandoSugerencias, setRecargandoSugerencias] = useState(false);

  const [showModalConfirmacion, setShowModalConfirmacion] = useState(false);
  const [cargandoCrearOrdenes, setCargandoCrearOrdenes] = useState(false);

  // Estados para modal de notificación
  const [showModalNotificacion, setShowModalNotificacion] = useState(false);
  const [notificacion, setNotificacion] = useState<{
    tipo: 'success' | 'error' | 'warning' | 'info';
    titulo: string;
    mensaje: string;
  } | null>(null);
  const {
    sugerencias,
    ordenes: _ordenes,
    historial,
    cargandoSugerencias,
    // cargandoOrdenes,
    cargandoHistorial,
    errorSugerencias,
    errorOrdenes: _errorOrdenes,
    errorHistorial,
    generarSugerencias,
    // crearOrdenesDesdesugerencias,
    actualizarEstadoOrden: _actualizarEstadoOrden,
    marcarComoRecibida: _marcarComoRecibida,
    recargarDatos,
  } = useCompras();

  // Hook para notificaciones
  const { success: toastSuccess, error: toastError, addToast } = useToast();

  // Hooks para órdenes de compra
  const {
    data: ordenesData,
    isLoading: ordenesLoading,
    error: ordenesError,
    refetch: refetchOrdenes,
  } = useOrdenesCompra();

  // Hook para obtener proveedores (para cambio de proveedor en sugerencias)
  // limit alto: el modal de cambio de proveedor busca por id en la lista
  // local; si la lista venía paginada (~10) y el proveedor seleccionado no
  // estaba ahí, el lookup fallaba.
  const { data: proveedoresData } = useProveedores({ limit: 500 });
  const proveedores = proveedoresData?.data || [];

  const crearOrdenMutation = useCrearOrdenCompra();
  const enviarOrdenMutation = useEnviarOrdenCompra();
  const cancelarOrdenMutation = useCancelarOrdenCompra();
  // const _recepcionarOrdenMutation = useRecepcionarOrdenCompra(); // Para uso futuro

  // Obtener órdenes de la API
  const ordenesCompletas = ordenesData?.data || [];

  // Funciones para manejar sugerencias editables
  const inicializarSugerenciasEditables = () => {
    console.log('🔄 Inicializando sugerencias editables');

    if (sugerencias) {
      const copiaEditables = JSON.parse(JSON.stringify(sugerencias));

      // Log para depurar
      console.log('📊 Datos originales:', sugerencias);
      console.log('� Resumen original:', sugerencias.resumen);
      console.log('�📋 Copia para edición:', copiaEditables);

      // Verificar que costo_unitario existe
      Object.entries(copiaEditables.sugerencias_por_proveedor).forEach(
        ([_proveedor, datos]: [string, any]) => {
          datos.ingredientes.forEach((ing: any) => {
            console.log(
              `✓ ${ing.nombre_ingrediente}: costo_unitario=${ing.costo_unitario}, costo_estimado=${ing.costo_estimado}`
            );
          });
        }
      );

      setSugerenciasEditables(copiaEditables);

      // Inicializar fecha de entrega (+1 día)
      const mañana = new Date();
      mañana.setDate(mañana.getDate() + 1);
      setFechaEntregaDeseada(fechaLocalConOffset(1));

      // Seleccionar todos los ingredientes por defecto
      const todosLosIngredientes = new Set<string>();
      Object.entries(sugerencias.sugerencias_por_proveedor).forEach(
        ([proveedor, datos]) => {
          datos.ingredientes.forEach((ingrediente) => {
            todosLosIngredientes.add(
              `${proveedor}-${ingrediente.ingrediente_id}`
            );
          });
        }
      );

      console.log(
        '✅ Ingredientes seleccionados por defecto:',
        Array.from(todosLosIngredientes)
      );
      setIngredientesSeleccionados(todosLosIngredientes);
    }
  };

  // Wrapper para generar sugerencias y activar automáticamente el modo editable
  const handleGenerarSugerencias = async (parametros: any) => {
    // Ejecutar la generación de sugerencias
    await generarSugerencias(parametros);

    // Note: La activación del modo editable se hará en useEffect cuando cambien las sugerencias
  };

  // Efecto para activar automáticamente el modo editable cuando se generen nuevas sugerencias
  useEffect(() => {
    console.log('🔍 useEffect sugerencias:', {
      sugerencias: !!sugerencias,
      sugerenciasEditables: !!sugerenciasEditables,
      shouldInit: !!sugerencias,
    });

    // Siempre reinicializar cuando lleguen nuevas sugerencias
    if (sugerencias) {
      console.log(
        '✅ Reinicializando sugerencias editables por nuevas sugerencias'
      );
      inicializarSugerenciasEditables();

      // Si se estaban recargando las sugerencias, ya se completó
      if (recargandoSugerencias) {
        setRecargandoSugerencias(false);
        console.log('✅ Recarga de sugerencias completada');
      }
    }
  }, [sugerencias, recargandoSugerencias]); // Agregamos recargandoSugerencias como dependencia

  // Efecto para generar sugerencias automáticamente al cargar el módulo por primera vez
  useEffect(() => {
    if (!sugerencias && !cargandoSugerencias && !errorSugerencias) {
      console.log(
        '🚀 Generando sugerencias automáticamente con parámetros por defecto'
      );
      const parametrosDefecto = {
        diasAdelante: 2,
        porcentajeExtra: 0,
        incluirStock: true,
      };
      generarSugerencias(parametrosDefecto, false); // false = no mostrar toast
    }
  }, []); // Solo ejecutar al montar el componente

  const actualizarPrecioIngrediente = (
    proveedor: string,
    ingredienteId: number,
    nuevoPrecioUnitario: number
  ) => {
    if (!sugerenciasEditables) return;

    setSugerenciasEditables((prev: any) => {
      const nuevasSugerencias = { ...prev };
      const proveedorData =
        nuevasSugerencias.sugerencias_por_proveedor[proveedor];

      if (proveedorData) {
        const ingredienteIndex = proveedorData.ingredientes.findIndex(
          (i: any) => i.ingrediente_id === ingredienteId
        );

        if (ingredienteIndex >= 0) {
          const ingrediente = proveedorData.ingredientes[ingredienteIndex];

          // Actualizar precio unitario
          ingrediente.costo_unitario = nuevoPrecioUnitario;

          // Recalcular costo estimado total (cantidad × precio unitario)
          ingrediente.costo_estimado =
            ingrediente.cantidad_sugerida * nuevoPrecioUnitario;

          // Recalcular total del proveedor
          const nuevoTotal = proveedorData.ingredientes.reduce(
            (sum: number, ing: any) => {
              return sum + (ing.costo_estimado || 0);
            },
            0
          );
          proveedorData.total_estimado = nuevoTotal;

          // Recalcular total general
          nuevasSugerencias.resumen.costo_total_estimado = Object.values(
            nuevasSugerencias.sugerencias_por_proveedor
          ).reduce((sum: number, p: any) => sum + p.total_estimado, 0);
        }
      }

      return nuevasSugerencias;
    });
  };

  // Función para actualizar cantidad sugerida de un ingrediente
  const actualizarCantidadIngrediente = (
    proveedor: string,
    ingredienteId: number,
    nuevaCantidad: number
  ) => {
    if (!sugerenciasEditables) return;

    setSugerenciasEditables((prev: any) => {
      const nuevasSugerencias = { ...prev };
      const proveedorData =
        nuevasSugerencias.sugerencias_por_proveedor[proveedor];

      if (proveedorData) {
        const ingredienteIndex = proveedorData.ingredientes.findIndex(
          (i: any) => i.ingrediente_id === ingredienteId
        );

        if (ingredienteIndex >= 0) {
          const ingrediente = proveedorData.ingredientes[ingredienteIndex];

          // Actualizar cantidad sugerida
          ingrediente.cantidad_sugerida = nuevaCantidad;

          // Recalcular costo estimado total (cantidad × precio unitario)
          ingrediente.costo_estimado =
            nuevaCantidad * (ingrediente.costo_unitario || 0);

          // Recalcular total del proveedor
          const nuevoTotal = proveedorData.ingredientes.reduce(
            (sum: number, ing: any) => {
              return sum + (ing.costo_estimado || 0);
            },
            0
          );
          proveedorData.total_estimado = nuevoTotal;

          // Recalcular total general
          nuevasSugerencias.resumen.costo_total_estimado = Object.values(
            nuevasSugerencias.sugerencias_por_proveedor
          ).reduce((sum: number, p: any) => sum + p.total_estimado, 0);
        }
      }

      return nuevasSugerencias;
    });
  };

  // Función para abrir modal de cambio de proveedor
  const abrirModalCambioProveedor = (
    proveedorActual: string,
    ingredienteId: number,
    nombreIngrediente: string,
    categoriaIngrediente?: string
  ) => {
    setModalCambioProveedor({
      abierto: true,
      proveedorActual,
      ingredienteId,
      nombreIngrediente,
      categoriaIngrediente,
    });
  };

  // Función para cerrar modal de cambio de proveedor
  const cerrarModalCambioProveedor = () => {
    setModalCambioProveedor(null);
  };

  // Confirmar cambio de proveedor desde el modal — delega al handler
  // unificado moverIngredienteAOtroProveedor para que el flujo de modal y
  // el de drag & drop compartan exactamente la misma lógica (optimistic
  // update, PUT, rollback, banner Deshacer).
  const confirmarCambioProveedor = async (nuevoProveedorId: number) => {
    if (!modalCambioProveedor || !token) {
      toastError('Error: No se pudo procesar el cambio de proveedor');
      return;
    }
    const nuevoProveedor = proveedores.find(
      (p: any) => p.id === nuevoProveedorId
    );
    const nombreNuevoProveedor =
      nuevoProveedor?.nombre || 'el nuevo proveedor';
    cerrarModalCambioProveedor();
    await moverIngredienteAOtroProveedor(
      modalCambioProveedor.ingredienteId,
      modalCambioProveedor.nombreIngrediente,
      modalCambioProveedor.proveedorActual,
      nombreNuevoProveedor,
      nuevoProveedorId
    );
  };

  // Snapshot del último movimiento de ingrediente, para permitir un
  // botón "Deshacer" en el banner inline (ver render del banner abajo).
  // Si es null, no hay nada que deshacer.
  const [ultimoMovimiento, setUltimoMovimiento] = useState<{
    ingredienteId: number;
    nombreIngrediente: string;
    proveedorOrigenNombre: string;
    proveedorOrigenId: number | null;
    proveedorDestinoNombre: string;
    snapshot: any;
  } | null>(null);
  const undoTimeoutRef = useRef<number | null>(null);

  // Handler unificado para mover un ingrediente de un proveedor a otro.
  // Lo usan tanto el modal de "Cambiar proveedor" como el drag & drop.
  // Hace optimistic update local, persiste en backend, y deja preparado
  // el banner Deshacer durante 5 segundos.
  const moverIngredienteAOtroProveedor = async (
    ingredienteId: number,
    nombreIngrediente: string,
    proveedorOrigenNombre: string,
    proveedorDestinoNombre: string,
    proveedorDestinoId: number
  ) => {
    if (proveedorOrigenNombre === proveedorDestinoNombre) return;

    if (undoTimeoutRef.current !== null) {
      window.clearTimeout(undoTimeoutRef.current);
      undoTimeoutRef.current = null;
    }

    const snapshot = sugerenciasEditables;
    const proveedorOriginal = proveedores.find(
      (p: any) => p.nombre === proveedorOrigenNombre
    );

    moverIngredienteEntreProveedoresLocal(
      ingredienteId,
      proveedorOrigenNombre,
      proveedorDestinoNombre
    );

    try {
      const { api } = await import('../services/api');
      // Endpoint específico de Compras (S3.2): solo modifica
      // proveedor_principal_id, no toca el resto del ingrediente.
      await api.patch(`/sugerencias-compra/${ingredienteId}/proveedor`, {
        proveedor_id: proveedorDestinoId,
      });

      toastSuccess(
        'Proveedor cambiado',
        `${nombreIngrediente} → ${proveedorDestinoNombre}`
      );

      setUltimoMovimiento({
        ingredienteId,
        nombreIngrediente,
        proveedorOrigenNombre,
        proveedorOrigenId: proveedorOriginal?.id ?? null,
        proveedorDestinoNombre,
        snapshot,
      });
      undoTimeoutRef.current = window.setTimeout(() => {
        setUltimoMovimiento(null);
        undoTimeoutRef.current = null;
      }, 5000);
    } catch (error) {
      console.error('Error al cambiar proveedor:', error);
      setSugerenciasEditables(snapshot);
      toastError(
        'Error al cambiar proveedor',
        error instanceof Error ? error.message : 'Ocurrió un error inesperado'
      );
    }
  };

  // Handler del banner Deshacer.
  const deshacerUltimoMovimiento = async () => {
    if (!ultimoMovimiento) return;
    if (undoTimeoutRef.current !== null) {
      window.clearTimeout(undoTimeoutRef.current);
      undoTimeoutRef.current = null;
    }
    const m = ultimoMovimiento;
    setUltimoMovimiento(null);
    setSugerenciasEditables(m.snapshot);

    try {
      const { api } = await import('../services/api');
      // Acepta proveedor_id null (mueve a "Sin proveedor"). El endpoint
      // nuevo lo soporta nativamente, sin necesidad de mandar el resto
      // del ingrediente.
      await api.patch(`/sugerencias-compra/${m.ingredienteId}/proveedor`, {
        proveedor_id: m.proveedorOrigenId,
      });
      toastSuccess(
        'Cambio revertido',
        `${m.nombreIngrediente} volvió a ${m.proveedorOrigenNombre}`
      );
    } catch (err) {
      toastError(
        'No se pudo revertir',
        err instanceof Error ? err.message : 'Error inesperado'
      );
    }
  };

  // Mueve un ingrediente entre proveedores dentro de `sugerenciasEditables`
  // sin tocar el backend. Reusable por el flujo de drag & drop y por el
  // modal de cambio manual. Si el proveedor destino no existía en la
  // estructura (caso típico al asignar desde "Sin proveedor"), crea la
  // entrada nueva. Si el origen queda vacío, lo elimina del listado.
  // Recalcula totales del proveedor origen, destino y resumen general.
  const moverIngredienteEntreProveedoresLocal = (
    ingredienteId: number,
    proveedorOrigenNombre: string,
    proveedorDestinoNombre: string
  ) => {
    if (!sugerenciasEditables) return;
    if (proveedorOrigenNombre === proveedorDestinoNombre) return;

    setSugerenciasEditables((prev: any) => {
      if (!prev) return prev;

      // Clonar profundo lo que vamos a mutar.
      const nuevo = {
        ...prev,
        sugerencias_por_proveedor: { ...prev.sugerencias_por_proveedor },
        resumen: { ...prev.resumen },
      };

      const origen = nuevo.sugerencias_por_proveedor[proveedorOrigenNombre];
      if (!origen) return prev;

      const idx = origen.ingredientes.findIndex(
        (i: any) => i.ingrediente_id === ingredienteId
      );
      if (idx < 0) return prev;

      const [ingredienteMovido] = origen.ingredientes.splice(idx, 1);
      // Preservamos campos editados (cantidad/precio) y solo actualizamos
      // el nombre del proveedor principal para que el chip y los lookups
      // posteriores funcionen.
      ingredienteMovido.proveedor_principal = proveedorDestinoNombre;

      // Recalcular total del origen.
      origen.total_estimado = origen.ingredientes.reduce(
        (sum: number, ing: any) => sum + (ing.costo_estimado || 0),
        0
      );

      // Si el origen quedó sin ingredientes, lo eliminamos del listado.
      if (origen.ingredientes.length === 0) {
        delete nuevo.sugerencias_por_proveedor[proveedorOrigenNombre];
      } else {
        nuevo.sugerencias_por_proveedor[proveedorOrigenNombre] = {
          ...origen,
        };
      }

      // Insertar/actualizar destino.
      const destinoExistente =
        nuevo.sugerencias_por_proveedor[proveedorDestinoNombre];
      const destino = destinoExistente
        ? {
            ...destinoExistente,
            ingredientes: [
              ...destinoExistente.ingredientes,
              ingredienteMovido,
            ],
          }
        : {
            ingredientes: [ingredienteMovido],
            total_estimado: 0,
          };
      destino.total_estimado = destino.ingredientes.reduce(
        (sum: number, ing: any) => sum + (ing.costo_estimado || 0),
        0
      );
      nuevo.sugerencias_por_proveedor[proveedorDestinoNombre] = destino;

      // Recalcular resumen general.
      nuevo.resumen.costo_total_estimado = Object.values(
        nuevo.sugerencias_por_proveedor
      ).reduce(
        (sum: number, p: any) => sum + (p.total_estimado || 0),
        0
      );
      nuevo.resumen.total_proveedores = Object.keys(
        nuevo.sugerencias_por_proveedor
      ).length;

      return nuevo;
    });

    // Limpiar selección del ingrediente movido bajo el proveedor anterior
    // (la key compuesta ya no aplica al nuevo grupo).
    setIngredientesSeleccionados((prev) => {
      const next = new Set(prev);
      next.delete(`${proveedorOrigenNombre}-${ingredienteId}`);
      return next;
    });
  };

  const toggleSeleccionIngrediente = (
    proveedor: string,
    ingredienteId: number
  ) => {
    const key = `${proveedor}-${ingredienteId}`;
    setIngredientesSeleccionados((prev) => {
      const nuevaSeleccion = new Set(prev);
      if (nuevaSeleccion.has(key)) {
        nuevaSeleccion.delete(key);
      } else {
        nuevaSeleccion.add(key);
      }
      return nuevaSeleccion;
    });
  };

  const prepararDatosParaCrearOrdenes = () => {
    console.log('🔍 Preparando datos para crear órdenes');
    console.log('   - sugerenciasEditables:', !!sugerenciasEditables);
    console.log(
      '   - ingredientesSeleccionados size:',
      ingredientesSeleccionados.size
    );
    console.log(
      '   - ingredientesSeleccionados:',
      Array.from(ingredientesSeleccionados)
    );

    if (!sugerenciasEditables) return null;

    const sugerenciasParaEnviar = Object.entries(
      sugerenciasEditables.sugerencias_por_proveedor
    )
      .map(([proveedor, datos]: [string, any]) => ({
        proveedor: proveedor,
        proveedor_id: datos.proveedor_id, // Agregar el ID del proveedor
        fecha_entrega_esperada: fechaEntregaDeseada,
        ingredientes: datos.ingredientes
          .filter((ingrediente: any) => {
            const key = `${proveedor}-${ingrediente.ingrediente_id}`;
            return ingredientesSeleccionados.has(key);
          })
          .map((ingrediente: any) => ({
            ingrediente_id: ingrediente.ingrediente_id,
            cantidad_sugerida: ingrediente.cantidad_sugerida,
            costo_unitario: ingrediente.costo_unitario,
            detalles_pedidos: ingrediente.detalles_pedidos || [],
          })),
      }))
      .filter((proveedor: any) => proveedor.ingredientes.length > 0);

    return {
      sugerencias: sugerenciasParaEnviar,
      observaciones_generales: `Órdenes generadas desde sugerencias del ${formatearFechaCorta(obtenerFechaActualLocal())}`,
      actualizar_precios: true,
    };
  };

  const handleCrearOrdenesDesdesugerencias = async () => {
    // Inicializar sugerencias editables si no están inicializadas
    if (!sugerenciasEditables && sugerencias) {
      inicializarSugerenciasEditables();
    }

    const datosParaEnviar = prepararDatosParaCrearOrdenes();
    if (!datosParaEnviar || datosParaEnviar.sugerencias.length === 0) {
      mostrarNotificacion(
        'warning',
        'Sin ingredientes seleccionados',
        'Por favor selecciona al menos un ingrediente para crear órdenes de compra.'
      );
      return;
    }

    setShowModalConfirmacion(true);
  };

  const confirmarCreacionOrdenes = async () => {
    // Guard de idempotencia: doble-click rápido en el botón confirmar no
    // dispara dos batches concurrentes. cargandoCrearOrdenes y
    // crearOrdenMutation.isPending cubren ambos lados (estado del componente
    // y estado de React Query) por si la mutation queda en flight más allá
    // del setCargandoCrearOrdenes(false).
    if (cargandoCrearOrdenes || crearOrdenMutation.isPending) return;

    const datosParaEnviar = prepararDatosParaCrearOrdenes();
    if (!datosParaEnviar) return;

    try {
      setCargandoCrearOrdenes(true);

      const ordenesCreadas: any[] = [];
      let totalItems = 0;
      let totalEstimado = 0;
      let errores = 0;

      // Crear una orden por cada proveedor
      for (const sugerenciaProveedor of datosParaEnviar.sugerencias) {
        try {
          // Generar número de orden único
          const ahora = new Date();
          const numero_orden = `OC-${ahora.getFullYear()}${String(ahora.getMonth() + 1).padStart(2, '0')}${String(ahora.getDate()).padStart(2, '0')}-${String(ahora.getHours()).padStart(2, '0')}${String(ahora.getMinutes()).padStart(2, '0')}${String(ahora.getSeconds()).padStart(2, '0')}-${ordenesCreadas.length}`;

          const detalles = sugerenciaProveedor.ingredientes.map((ing: any) => ({
            ingrediente_id: ing.ingrediente_id,
            cantidad_solicitada: ing.cantidad_sugerida,
            precio_unitario: ing.costo_unitario,
          }));

          // Asegurar que proveedor_id sea un número válido
          const proveedorId = sugerenciaProveedor.proveedor_id || 1; // Fallback a "Proveedor Desconocido"

          const payload = {
            numero_orden,
            proveedor_id: proveedorId,
            fecha_solicitud: obtenerFechaActualLocal(),
            fecha_entrega_esperada: sugerenciaProveedor.fecha_entrega_esperada,
            observaciones:
              datosParaEnviar.observaciones_generales ||
              `Orden generada desde sugerencias - ${sugerenciaProveedor.ingredientes.length} ingrediente(s)`,
            solicitado_por: user?.id || 1,
            detalles,
          };

          console.log(
            `📦 Creando orden para ${sugerenciaProveedor.proveedor}:`,
            payload
          );

          const resultado = await crearOrdenMutation.mutateAsync(payload);
          ordenesCreadas.push(resultado);
          totalItems += detalles.length;

          // Calcular total estimado
          const totalOrden = detalles.reduce(
            (sum: number, det: any) =>
              sum + det.cantidad_solicitada * det.precio_unitario,
            0
          );
          totalEstimado += totalOrden;
        } catch (error) {
          console.error(
            `Error al crear orden para ${sugerenciaProveedor.proveedor}:`,
            error
          );
          errores++;
        }
      }

      // Mostrar mensaje de éxito
      if (ordenesCreadas.length > 0) {
        // Toast persistente con acción "Ver órdenes": el usuario decide
        // explícitamente cuándo ir a la otra pestaña, en vez de que el
        // sistema lo arrastre.
        addToast({
          type: errores > 0 ? 'warning' : 'success',
          title:
            errores > 0
              ? '¡Órdenes creadas parcialmente!'
              : '✅ Órdenes creadas',
          message:
            `${ordenesCreadas.length} OC(s) en estado EMITIDA — ${totalItems} items, total $${formatearNumero(totalEstimado)}.\n` +
            `Cuando llegue la mercadería, ve a Órdenes y presioná "Recepcionar".` +
            (errores > 0 ? `\n⚠️ ${errores} orden(es) fallaron` : ''),
          duration: 8000,
          actions: [
            {
              label: 'Ver órdenes',
              onClick: () => setVistaActiva('ordenes'),
              variant: 'primary',
            },
          ],
        });

        // Invalidar las queries de React Query para refrescar las órdenes automáticamente
        queryClient.invalidateQueries({ queryKey: ordenesCompraKeys.lists() });

        // También recargar los datos del hook useCompras por si acaso
        await recargarDatos();

        // Esperar un momento para que se procesen las invalidaciones
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Quedarse en la pestaña Sugerencias para que el usuario vea su acción
        // reflejada (cards transformadas a "OC EMITIDA"). El cambio de tab
        // automático confundía: hacía pensar que la creación había fallado y
        // empujaba a clickear "Recepcionar" sin haber recibido la mercadería.

        // Cerrar modal y limpiar estado
        setShowModalConfirmacion(false);
        setSugerenciasEditables(null);
        setIngredientesSeleccionados(new Set());
      } else {
        mostrarNotificacion(
          'error',
          'Error al crear órdenes',
          'No se pudo crear ninguna orden de compra. Por favor intenta nuevamente.'
        );
      }
    } catch (error) {
      console.error('Error al crear órdenes:', error);
      mostrarNotificacion(
        'error',
        'Error al crear órdenes',
        'No se pudieron crear las órdenes de compra. Por favor intenta nuevamente.'
      );
    } finally {
      setCargandoCrearOrdenes(false);
    }
  };

  // Funciones para manejar órdenes de compra
  const formatearMoneda = (cantidad: number): string => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(cantidad);
  };

  const formatearNumero = (numero: number): string => {
    return new Intl.NumberFormat('es-CL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numero);
  };

  const parsearNumero = (texto: string): number => {
    // Remover separadores de miles y convertir a número
    return parseInt(texto.replace(/\./g, '')) || 0;
  };

  // Función para mostrar notificaciones elegantes
  const mostrarNotificacion = (
    tipo: 'success' | 'error' | 'warning' | 'info',
    titulo: string,
    mensaje: string
  ) => {
    setNotificacion({ tipo, titulo, mensaje });
    setShowModalNotificacion(true);
  };

  const handleCrearOrden = () => {
    setShowFormulario(true);
  };

  const handleGuardarOrden = async (datosWizard: any) => {
    try {
      // Transformar items a detalles (el backend espera 'detalles', no 'items')
      const detalles = datosWizard.items.map((item: any) => ({
        ingrediente_id: parseInt(item.ingredienteId),
        cantidad_solicitada: item.cantidad,
        precio_unitario: item.precioUnitario,
      }));

      // Generar número de orden único
      const ahora = new Date();
      const numero_orden = `OC-${ahora.getFullYear()}${String(ahora.getMonth() + 1).padStart(2, '0')}${String(ahora.getDate()).padStart(2, '0')}-${String(ahora.getHours()).padStart(2, '0')}${String(ahora.getMinutes()).padStart(2, '0')}${String(ahora.getSeconds()).padStart(2, '0')}`;

      // Fecha de solicitud (hoy)
      const fecha_solicitud = obtenerFechaActualLocal();

      // Fecha de entrega esperada
      let fecha_entrega_esperada = datosWizard.fechaEntregaEsperada;
      if (!fecha_entrega_esperada) {
        const mañana = new Date();
        mañana.setDate(mañana.getDate() + 1);
        fecha_entrega_esperada = fechaLocalConOffset(1);
      }

      // Obtener ID del usuario actual
      const solicitado_por = user?.id || 1; // Fallback a 1 si no hay usuario

      // Payload completo según el schema del backend
      const payload = {
        numero_orden,
        proveedor_id: Number(datosWizard.proveedor.id),
        fecha_solicitud,
        fecha_entrega_esperada,
        observaciones: datosWizard.observaciones || '',
        solicitado_por,
        detalles,
      };

      console.log('Enviando orden de compra:', payload);

      await crearOrdenMutation.mutateAsync(payload);

      setShowFormulario(false);
    } catch (error) {
      console.error('Error al crear orden:', error);
    }
  };

  // Función helper para verificar si existe una orden para un proveedor y su estado
  const obtenerOrdenProveedor = (
    nombreProveedor: string
  ): {
    tieneOrden: boolean;
    ordenId?: string;
    estado?: string;
  } => {
    // Filtrar órdenes del proveedor EXCLUYENDO las canceladas Y recibidas
    // Solo considerar órdenes en proceso: borrador, enviada
    // Las canceladas y recibidas ya completaron su ciclo, no deben bloquear nuevas OCs
    const ordenesProveedor = ordenesCompletas.filter(
      (orden: any) =>
        orden.proveedor === nombreProveedor &&
        orden.estado !== 'cancelada' && // ✅ Ignorar canceladas
        orden.estado !== 'recibida' // ✅ Ignorar recibidas (ya están en stock)
    );

    // Si hay órdenes activas (borrador o enviada), tomar la más reciente
    if (ordenesProveedor.length > 0) {
      // Ordenar por fecha de creación descendente y tomar la primera
      const ordenMasReciente = ordenesProveedor.sort((a: any, b: any) => {
        const fechaA = new Date(a.created_at || a.fecha_solicitud).getTime();
        const fechaB = new Date(b.created_at || b.fecha_solicitud).getTime();
        return fechaB - fechaA; // Más reciente primero
      })[0];

      return {
        tieneOrden: true,
        ordenId: ordenMasReciente?.id,
        estado: ordenMasReciente?.estado,
      };
    }

    // Si no hay órdenes activas (solo canceladas/recibidas o ninguna), permitir crear nueva
    return {
      tieneOrden: false,
    };
  };

  // Mantener compatibilidad con código existente
  const tieneOrdenEnBorrador = (nombreProveedor: string) => {
    const info = obtenerOrdenProveedor(nombreProveedor);
    return {
      tieneBorrador: info.tieneOrden && info.estado === 'borrador',
      ordenId: info.ordenId,
    };
  };

  const handleCrearOrdenProveedor = async (nombreProveedor: string) => {
    if (!sugerenciasEditables) return;

    try {
      setCargandoCrearOrdenes(true);

      const datosProveedor =
        sugerenciasEditables.sugerencias_por_proveedor[nombreProveedor];

      console.log('🔍 Debug - datosProveedor:', datosProveedor);
      console.log('🔍 Debug - proveedor_id:', datosProveedor?.proveedor_id);

      if (!datosProveedor || !datosProveedor.ingredientes.length) {
        toastError('No hay ingredientes para este proveedor');
        return;
      }

      // Filtrar solo los ingredientes seleccionados de este proveedor
      const ingredientesSeleccionadosProveedor =
        datosProveedor.ingredientes.filter((ing: any) =>
          ingredientesSeleccionados.has(
            `${nombreProveedor}-${ing.ingrediente_id}`
          )
        );

      if (ingredientesSeleccionadosProveedor.length === 0) {
        toastError(
          'Debes seleccionar al menos un ingrediente de este proveedor'
        );
        return;
      }

      // Generar número de orden único
      const ahora = new Date();
      const numero_orden = `OC-${ahora.getFullYear()}${String(ahora.getMonth() + 1).padStart(2, '0')}${String(ahora.getDate()).padStart(2, '0')}-${String(ahora.getHours()).padStart(2, '0')}${String(ahora.getMinutes()).padStart(2, '0')}${String(ahora.getSeconds()).padStart(2, '0')}`;

      const detalles = ingredientesSeleccionadosProveedor.map((ing: any) => ({
        ingrediente_id: ing.ingrediente_id,
        cantidad_solicitada: ing.cantidad_sugerida,
        precio_unitario: ing.costo_unitario,
      }));

      // Validar que el proveedor_id exista
      const proveedorId = datosProveedor.proveedor_id;

      if (!proveedorId) {
        toastError(
          `❌ ERROR: No se pudo obtener el ID del proveedor "${nombreProveedor}". ` +
            `Por favor, verifica que el ingrediente tenga un proveedor principal asignado en el catálogo.`
        );
        setCargandoCrearOrdenes(false);
        return;
      }

      const payload = {
        numero_orden,
        proveedor_id: proveedorId,
        fecha_solicitud: obtenerFechaActualLocal(),
        fecha_entrega_esperada: fechaEntregaDeseada,
        observaciones: `Orden generada desde sugerencias - ${ingredientesSeleccionadosProveedor.length} ingrediente(s)`,
        solicitado_por: user?.id || 1,
        detalles,
      };

      console.log('📦 Creando orden para proveedor:', nombreProveedor, payload);

      await crearOrdenMutation.mutateAsync(payload);

      // 🔄 Refrescar inmediatamente la lista de órdenes
      await refetchOrdenes();

      toastSuccess(`Orden de compra creada para ${nombreProveedor}`);

      // Limpiar selección de este proveedor
      const nuevaSeleccion = new Set(ingredientesSeleccionados);
      ingredientesSeleccionadosProveedor.forEach((ing: any) => {
        nuevaSeleccion.delete(`${nombreProveedor}-${ing.ingrediente_id}`);
      });
      setIngredientesSeleccionados(nuevaSeleccion);
    } catch (error) {
      console.error('Error al crear orden para proveedor:', error);
      toastError('Error al crear la orden de compra');
    } finally {
      setCargandoCrearOrdenes(false);
    }
  };

  const handleVerDetalles = (ordenId: string) => {
    setOrdenSeleccionadaId(ordenId);
    setShowDetalles(true);
  };

  const handleCerrarDetalles = () => {
    setShowDetalles(false);
    setOrdenSeleccionadaId('');
  };

  const handleEditarOrden = (ordenId: string) => {
    setOrdenEditarId(ordenId);
    setShowEditar(true);
  };

  // Función para el modal de detalles que recibe un objeto
  const handleEditarOrdenFromDetalle = (orden: any) => {
    setOrdenEditarId(orden.id);
    setShowEditar(true);
    setShowDetalles(false); // Cerrar el modal de detalles
  };

  const handleCerrarEditar = () => {
    setShowEditar(false);
    setOrdenEditarId('');
  };

  const handleCambiarEstado = async (
    ordenId: string,
    nuevoEstado: string,
    motivo?: string
  ) => {
    try {
      switch (nuevoEstado) {
        case 'enviada':
          await enviarOrdenMutation.mutateAsync(ordenId);
          break;
        case 'cancelada':
          await cancelarOrdenMutation.mutateAsync({
            id: ordenId,
            motivo: motivo || undefined,
          });
          break;
        case 'recibida':
          setOrdenRecepcionId(ordenId);
          setShowRecepcion(true);
          break;
        default:
          console.log(`Cambiar estado a ${nuevoEstado} para orden ${ordenId}`);
      }
      handleCerrarDetalles();
    } catch (error) {
      console.error('Error al cambiar estado:', error);
    }
  };

  const handleCerrarRecepcion = () => {
    setShowRecepcion(false);
    setOrdenRecepcionId('');
  };

  const handleRecepcionar = (ordenId: string) => {
    setOrdenRecepcionId(ordenId);
    setShowRecepcion(true);
  };

  const estadoColor = (estado: string) => {
    switch (estado) {
      case 'borrador':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'enviada':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'recibida':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'parcial':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'cancelada':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const estadoIcon = (estado: string) => {
    switch (estado) {
      case 'borrador':
        return <Edit className="h-4 w-4" />;
      case 'enviada':
        return <Clock className="h-4 w-4" />;
      case 'recibida':
        return <CheckCircle className="h-4 w-4" />;
      case 'parcial':
        return <Package className="h-4 w-4" />;
      case 'cancelada':
        return <XCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const tabs = [
    {
      id: 'sugerencias' as const,
      label: 'Sugerencias',
      icon: TrendingUp,
      description: 'Ingredientes recomendados para comprar',
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      borderColor: 'border-blue-200 dark:border-blue-800',
    },
    {
      id: 'ordenes' as const,
      label: 'Órdenes de Compra',
      icon: FileText,
      description: 'Gestionar órdenes de compra activas',
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      borderColor: 'border-green-200 dark:border-green-800',
    },
    {
      id: 'facturas' as const,
      label: 'Facturas de Compra',
      icon: Receipt,
      description: 'Gestionar facturas de compra y actualizar stock',
      color: 'text-orange-600 dark:text-orange-400',
      bgColor: 'bg-orange-50 dark:bg-orange-900/20',
      borderColor: 'border-orange-200 dark:border-orange-800',
    },
    {
      id: 'historial' as const,
      label: 'Historial',
      icon: Package,
      description: 'Historial de compras y recepciones',
      color: 'text-purple-600 dark:text-purple-400',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
      borderColor: 'border-purple-200 dark:border-purple-800',
    },
  ];

  const tabActiva = tabs.find((tab) => tab.id === vistaActiva);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <PageHeader
        title="Gestión de Compras"
        helpSeccion="compras"
        subtitle="Administra sugerencias, órdenes de compra y recepciones de inventario"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Navigation Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = vistaActiva === tab.id;

                return (
                  <button
                    key={tab.id}
                    onClick={() => setVistaActiva(tab.id)}
                    className={`
                      group inline-flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                      ${
                        isActive
                          ? `${tab.color} border-current`
                          : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                      }
                    `}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {/* Tab Header */}
          {tabActiva && (
            <div
              className={`rounded-lg p-6 border ${tabActiva.bgColor} ${tabActiva.borderColor}`}
            >
              <div className="flex items-center space-x-3">
                <div
                  className={`p-2 rounded-lg bg-white dark:bg-gray-800 ${tabActiva.color}`}
                >
                  <tabActiva.icon className="h-6 w-6" />
                </div>
                <div>
                  <h2 className={`text-xl font-semibold ${tabActiva.color}`}>
                    {tabActiva.label}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    {tabActiva.description}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Content por vista */}
          {vistaActiva === 'sugerencias' && (
            <SugerenciasContent
              sugerencias={sugerencias}
              cargando={cargandoSugerencias || recargandoSugerencias}
              error={errorSugerencias}
              onGenerarSugerencias={handleGenerarSugerencias}
              onCrearOrdenes={handleCrearOrdenesDesdesugerencias}
              cargandoOrdenes={cargandoCrearOrdenes}
              // Props para edición
              sugerenciasEditables={sugerenciasEditables}
              ingredientesSeleccionados={ingredientesSeleccionados}
              fechaEntregaDeseada={fechaEntregaDeseada}
              onActualizarPrecio={actualizarPrecioIngrediente}
              onActualizarCantidad={actualizarCantidadIngrediente}
              onToggleSeleccion={toggleSeleccionIngrediente}
              onAbrirModalCambioProveedor={abrirModalCambioProveedor}
              onSetFechaEntrega={setFechaEntregaDeseada}
              onCrearOrdenProveedor={handleCrearOrdenProveedor}
              cargandoCrearOrdenes={cargandoCrearOrdenes}
              tieneOrdenEnBorrador={tieneOrdenEnBorrador}
              obtenerOrdenProveedor={obtenerOrdenProveedor}
              ordenesCompletas={ordenesCompletas}
              onVerDetallesOrden={handleVerDetalles}
              // Props para formateo
              formatearNumero={formatearNumero}
              parsearNumero={parsearNumero}
              onMoverIngrediente={moverIngredienteAOtroProveedor}
              proveedoresDisponibles={proveedores}
            />
          )}
          {vistaActiva === 'ordenes' && (
            <OrdenesContent
              ordenes={ordenesCompletas}
              cargando={ordenesLoading}
              error={ordenesError ? String(ordenesError) : null}
              filtros={filtros}
              onSetFiltros={setFiltros}
              paginaActual={paginaActual}
              elementosPorPagina={elementosPorPagina}
              onCambiarPagina={setPaginaActual}
              onCrearOrden={handleCrearOrden}
              onVerDetalles={handleVerDetalles}
              onEditarOrden={handleEditarOrden}
              onRecepcionar={handleRecepcionar}
              onCambiarEstado={handleCambiarEstado}
              formatearMoneda={formatearMoneda}
              estadoColor={estadoColor}
              estadoIcon={estadoIcon}
            />
          )}
          {vistaActiva === 'facturas' && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
              <FacturasCompra />
            </div>
          )}
          {vistaActiva === 'historial' && (
            <HistorialContent
              historial={historial}
              cargando={cargandoHistorial}
              error={errorHistorial}
              onRecargar={recargarDatos}
              onVerDetalles={handleVerDetalles}
            />
          )}
        </div>
      </div>

      {/* Modales */}
      {showFormulario && (
        <WizardOrdenCompra
          isOpen={showFormulario}
          onClose={() => setShowFormulario(false)}
          onSubmit={handleGuardarOrden}
        />
      )}

      {showDetalles && (
        <DetalleOrdenCompra
          isOpen={showDetalles}
          onClose={handleCerrarDetalles}
          ordenId={ordenSeleccionadaId}
          onEdit={handleEditarOrdenFromDetalle}
          onCambiarEstado={handleCambiarEstado}
          onNavigateToOrden={(ordenId) => {
            setOrdenSeleccionadaId(ordenId);
          }}
        />
      )}

      {showEditar && (
        <EditarOrdenCompra
          isOpen={showEditar}
          onClose={handleCerrarEditar}
          ordenId={ordenEditarId}
          onSuccess={handleCerrarEditar}
        />
      )}

      {showRecepcion && (
        <RecepcionOrdenCompra
          isOpen={showRecepcion}
          onClose={handleCerrarRecepcion}
          ordenId={ordenRecepcionId}
        />
      )}

      {/* Modal de Confirmación para Crear Órdenes */}
      {showModalConfirmacion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Confirmar Creación de Órdenes de Compra
            </h3>

            {prepararDatosParaCrearOrdenes() && (
              <div className="space-y-4">
                <p className="text-gray-600 dark:text-gray-400">
                  Se crearán las siguientes órdenes de compra:
                </p>

                {prepararDatosParaCrearOrdenes()!.sugerencias.map(
                  (proveedor: any, index: number) => (
                    <div
                      key={index}
                      className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4"
                    >
                      <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                        📦 {proveedor.proveedor}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {proveedor.ingredientes.length} ingredientes
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Fecha de entrega: {fechaEntregaDeseada}
                      </p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Total estimado: $
                        {formatearNumero(
                          proveedor.ingredientes.reduce(
                            (sum: number, ing: any) =>
                              sum + ing.cantidad_sugerida * ing.costo_unitario,
                            0
                          )
                        )}
                      </p>
                    </div>
                  )
                )}

                {/* Resumen total con IVA */}
                <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 border">
                  <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
                    📊 Resumen Total
                  </h4>
                  {(() => {
                    const subtotal =
                      prepararDatosParaCrearOrdenes()!.sugerencias.reduce(
                        (total: number, proveedor: any) =>
                          total +
                          proveedor.ingredientes.reduce(
                            (sum: number, ing: any) =>
                              sum + ing.cantidad_sugerida * ing.costo_unitario,
                            0
                          ),
                        0
                      );
                    const iva = subtotal * 0.19;
                    const totalConIva = subtotal + iva;

                    return (
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">
                            Subtotal:
                          </span>
                          <span className="text-gray-900 dark:text-gray-100">
                            ${formatearNumero(subtotal)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">
                            IVA (19%):
                          </span>
                          <span className="text-gray-900 dark:text-gray-100">
                            ${formatearNumero(iva)}
                          </span>
                        </div>
                        <div className="border-t border-gray-300 dark:border-gray-600 pt-2">
                          <div className="flex justify-between text-lg font-semibold">
                            <span className="text-gray-900 dark:text-gray-100">
                              Total:
                            </span>
                            <span className="text-green-600 dark:text-green-400">
                              ${formatearNumero(totalConIva)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    💡 Los precios editados se guardarán como nuevos precios de
                    referencia para los ingredientes.
                  </p>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowModalConfirmacion(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarCreacionOrdenes}
                disabled={cargandoCrearOrdenes}
                className="px-4 py-2 bg-green-600 dark:bg-green-700 text-white rounded-lg hover:bg-green-700 dark:hover:bg-green-600 transition-colors flex items-center space-x-2 disabled:opacity-50"
              >
                {cargandoCrearOrdenes ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    <span>Creando...</span>
                  </>
                ) : (
                  <>
                    <ShoppingCart className="h-4 w-4" />
                    <span>Confirmar y Crear Órdenes</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Banner Deshacer — visible 5 s tras mover un ingrediente entre
          proveedores. Permite revertir la última acción sin nuevo viaje
          al backend si fue dentro del timeout. */}
      {ultimoMovimiento && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-900 dark:bg-gray-800 text-white px-4 py-3 rounded-lg shadow-2xl flex items-center gap-4 max-w-md">
          <div className="text-sm">
            <span className="font-medium">
              {ultimoMovimiento.nombreIngrediente}
            </span>{' '}
            → {ultimoMovimiento.proveedorDestinoNombre}
          </div>
          <button
            type="button"
            onClick={deshacerUltimoMovimiento}
            className="text-blue-300 hover:text-blue-100 text-sm font-semibold underline-offset-2 hover:underline"
          >
            Deshacer
          </button>
        </div>
      )}

      {/* Modal de Notificación */}
      {showModalNotificacion && notificacion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-start space-x-3">
              {/* Icono según tipo */}
              <div
                className={`flex-shrink-0 ${
                  notificacion.tipo === 'success'
                    ? 'text-green-500'
                    : notificacion.tipo === 'error'
                      ? 'text-red-500'
                      : notificacion.tipo === 'warning'
                        ? 'text-yellow-500'
                        : 'text-blue-500'
                }`}
              >
                {notificacion.tipo === 'success' && (
                  <CheckCircle className="h-6 w-6" />
                )}
                {notificacion.tipo === 'error' && (
                  <XCircle className="h-6 w-6" />
                )}
                {notificacion.tipo === 'warning' && (
                  <AlertTriangle className="h-6 w-6" />
                )}
                {notificacion.tipo === 'info' && (
                  <FileText className="h-6 w-6" />
                )}
              </div>

              {/* Contenido */}
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  {notificacion.titulo}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 whitespace-pre-line">
                  {notificacion.mensaje}
                </p>
              </div>
            </div>

            {/* Botón de cerrar */}
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowModalNotificacion(false)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  notificacion.tipo === 'success'
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : notificacion.tipo === 'error'
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : notificacion.tipo === 'warning'
                        ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de cambio de proveedor */}
      {modalCambioProveedor?.abierto && (
        <ModalCambioProveedor
          isOpen={modalCambioProveedor.abierto}
          onClose={cerrarModalCambioProveedor}
          onConfirmar={confirmarCambioProveedor}
          proveedorActual={modalCambioProveedor.proveedorActual}
          nombreIngrediente={modalCambioProveedor.nombreIngrediente}
          categoriaIngrediente={modalCambioProveedor.categoriaIngrediente}
          proveedores={proveedores}
        />
      )}
    </div>
  );
};

// Componente para Sugerencias de Compra
interface SugerenciasContentProps {
  sugerencias: any | null;
  cargando: boolean;
  error: string | null;
  onGenerarSugerencias: (parametros: any) => Promise<void>;
  onCrearOrdenes: () => Promise<void>;
  cargandoOrdenes: boolean;
  // Props para edición
  sugerenciasEditables: any | null;
  ingredientesSeleccionados: Set<string>;
  fechaEntregaDeseada: string;
  onActualizarPrecio: (
    proveedor: string,
    ingredienteId: number,
    nuevoPrecio: number
  ) => void;
  onActualizarCantidad: (
    proveedor: string,
    ingredienteId: number,
    nuevaCantidad: number
  ) => void;
  onToggleSeleccion: (proveedor: string, ingredienteId: number) => void;
  onAbrirModalCambioProveedor: (
    proveedorActual: string,
    ingredienteId: number,
    nombreIngrediente: string,
    categoriaIngrediente?: string
  ) => void;
  onSetFechaEntrega: (fecha: string) => void;
  onCrearOrdenProveedor: (nombreProveedor: string) => Promise<void>;
  cargandoCrearOrdenes: boolean;
  tieneOrdenEnBorrador: (nombreProveedor: string) => {
    tieneBorrador: boolean;
    ordenId?: string;
  };
  obtenerOrdenProveedor: (nombreProveedor: string) => {
    tieneOrden: boolean;
    ordenId?: string;
    estado?: string;
  };
  ordenesCompletas: any[]; // Lista de todas las órdenes para verificar canceladas
  onVerDetallesOrden: (ordenId: string) => void;
  // Props para formateo
  formatearNumero: (numero: number) => string;
  parsearNumero: (texto: string) => number;
  // Drag & drop: handler que se ejecuta al soltar un ingrediente sobre
  // una card de proveedor distinta. La función valida proveedor_id y
  // dispara optimistic update + PUT al backend desde el padre.
  onMoverIngrediente: (
    ingredienteId: number,
    nombreIngrediente: string,
    proveedorOrigenNombre: string,
    proveedorDestinoNombre: string,
    proveedorDestinoId: number
  ) => Promise<void>;
  // Lista global de proveedores (usada por el handler de drop para
  // resolver el id del proveedor destino a partir del nombre).
  proveedoresDisponibles: any[];
}

const SugerenciasContent: React.FC<SugerenciasContentProps> = ({
  sugerencias,
  cargando,
  error,
  onGenerarSugerencias,
  onCrearOrdenes,
  cargandoOrdenes,
  // Props para edición
  sugerenciasEditables,
  ingredientesSeleccionados,
  fechaEntregaDeseada,
  onActualizarPrecio,
  onActualizarCantidad,
  onToggleSeleccion,
  onAbrirModalCambioProveedor,
  onSetFechaEntrega,
  onCrearOrdenProveedor,
  cargandoCrearOrdenes,
  tieneOrdenEnBorrador: _tieneOrdenEnBorrador,
  obtenerOrdenProveedor,
  ordenesCompletas,
  onVerDetallesOrden,
  // Props para formateo
  formatearNumero,
  parsearNumero,
  onMoverIngrediente,
  proveedoresDisponibles,
}) => {
  const [parametros, setParametros] = useState({
    diasAdelante: 2,
    porcentajeExtra: 0,
    incluirStock: true,
  });

  const handleGenerarSugerencias = () => {
    onGenerarSugerencias(parametros);
  };

  // Sensors de @dnd-kit:
  //   - PointerSensor con distance=5 para que un click en el handle no dispare
  //     drag involuntariamente (mouse / pen).
  //   - TouchSensor con delay=200ms + tolerance=5px para que un tap o un
  //     scroll vertical no se interpreten como drag en mobile/tablet.
  //   - KeyboardSensor para accesibilidad (Espacio para tomar, flechas,
  //     Espacio para soltar, Esc para cancelar).
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
    useSensor(KeyboardSensor)
  );

  // ID del ingrediente actualmente arrastrado, usado por el DragOverlay
  // para mostrar una preview flotante. null cuando no hay drag.
  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  const handleDragStart = (e: DragStartEvent) => {
    setActiveDragId(e.active.id.toString());
  };

  const handleDragEnd = async (e: DragEndEvent) => {
    setActiveDragId(null);
    if (!e.over) return;
    const overId = e.over.id.toString();
    const activeId = e.active.id.toString();
    if (!overId.startsWith('provider:')) return;
    if (!activeId.startsWith('item:')) return;

    const proveedorDestinoNombre = overId.slice('provider:'.length);
    // activeId formato: item:<proveedor>|<ingredienteId>
    const itemKey = activeId.slice('item:'.length);
    const sepIdx = itemKey.lastIndexOf('|');
    if (sepIdx < 0) return;
    const proveedorOrigenNombre = itemKey.slice(0, sepIdx);
    const ingredienteId = parseInt(itemKey.slice(sepIdx + 1), 10);

    if (proveedorOrigenNombre === proveedorDestinoNombre) return;
    if (Number.isNaN(ingredienteId)) return;

    // Resolver id del proveedor destino. Sin id no podemos persistir.
    const proveedorDestino = proveedoresDisponibles.find(
      (p: any) => p.nombre === proveedorDestinoNombre
    );
    if (!proveedorDestino?.id) {
      // El destino "Sin proveedor" no es un proveedor real; el drop no
      // tiene a quién persistir.
      return;
    }

    // Recuperar el nombre del ingrediente para el toast.
    const datosOrigen =
      sugerenciasEditables?.sugerencias_por_proveedor?.[proveedorOrigenNombre];
    const ingrediente = datosOrigen?.ingredientes?.find(
      (i: any) => i.ingrediente_id === ingredienteId
    );
    const nombreIngrediente =
      ingrediente?.nombre_ingrediente || 'el ingrediente';

    await onMoverIngrediente(
      ingredienteId,
      nombreIngrediente,
      proveedorOrigenNombre,
      proveedorDestinoNombre,
      proveedorDestino.id
    );
  };

  return (
    <div className="space-y-6">
      {/* Métricas rápidas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Ingredientes Sugeridos
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {sugerencias?.resumen?.total_ingredientes || 0}
              </p>
            </div>
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <TrendingUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg p-6 border-2 border-green-200 dark:border-green-700 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-700 dark:text-green-300">
                💰 Costo Total Estimado
              </p>
              <p className="text-3xl font-bold text-green-900 dark:text-green-100">
                $
                {(() => {
                  const totalEditado =
                    sugerenciasEditables?.resumen?.costo_total_estimado;
                  const totalOriginal =
                    sugerencias?.resumen?.costo_total_estimado;
                  return formatearNumero(totalEditado ?? totalOriginal ?? 0);
                })()}
              </p>
              <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                Inversión requerida
              </p>
            </div>
            <div className="p-4 bg-green-200 dark:bg-green-800 rounded-xl">
              <DollarSign className="h-8 w-8 text-green-700 dark:text-green-300" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Proveedores
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {sugerencias?.resumen?.total_proveedores || 0}
              </p>
            </div>
            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Truck className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-6 border-2 border-blue-200 dark:border-blue-700 shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-lg font-semibold text-blue-700 dark:text-blue-300 mb-3 flex items-center">
                <TrendingUp className="h-5 w-5 mr-2" />
                Configuración Actual
              </p>
              <div className="space-y-2">
                <div className="flex items-center text-sm text-blue-700 dark:text-blue-300">
                  <Calendar className="h-4 w-4 mr-2" />
                  <span className="font-semibold">
                    {parametros.diasAdelante === 0
                      ? 'Hoy'
                      : `${parametros.diasAdelante} días`}
                  </span>
                </div>
                <div className="flex items-center text-sm text-blue-700 dark:text-blue-300">
                  <Plus className="h-4 w-4 mr-2" />
                  <span className="font-semibold">
                    {parametros.porcentajeExtra}% extra
                  </span>
                </div>
                <div className="flex items-center text-sm text-blue-700 dark:text-blue-300">
                  {parametros.incluirStock ? (
                    <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                  ) : (
                    <XCircle className="h-4 w-4 mr-2 text-red-600" />
                  )}
                  <span className="font-semibold">
                    {parametros.incluirStock ? 'Con stock' : 'Sin stock'}
                  </span>
                </div>
              </div>
            </div>
            <div className="p-4 bg-blue-200 dark:bg-blue-800 rounded-xl">
              <TrendingUp className="h-8 w-8 text-blue-700 dark:text-blue-300" />
            </div>
          </div>
        </div>
      </div>

      {/* Controles de parámetros */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center space-x-2">
                <TrendingUp className="h-5 w-5" />
                <span>Configurar Sugerencias</span>
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Ajusta los parámetros para generar sugerencias personalizadas
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                <Calendar className="h-4 w-4 inline mr-1" />
                Días adelante
              </label>
              <input
                type="number"
                min="0"
                max="7"
                value={parametros.diasAdelante}
                onChange={(e) => {
                  const v = parseInt(e.target.value);
                  setParametros((prev) => ({
                    ...prev,
                    diasAdelante:
                      Number.isFinite(v) && v >= 0 ? Math.min(v, 7) : 0,
                  }));
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {parametros.diasAdelante === 0
                  ? 'Solo hoy'
                  : 'Proyección desde hoy'}
              </p>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                <Plus className="h-4 w-4 inline mr-1" />
                Porcentaje extra
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={parametros.porcentajeExtra}
                  onChange={(e) =>
                    setParametros((prev) => ({
                      ...prev,
                      porcentajeExtra: parseInt(e.target.value) || 0,
                    }))
                  }
                  className="w-full px-3 py-2 pr-8 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
                <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                  %
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Margen de seguridad adicional
              </p>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                <Package className="h-4 w-4 inline mr-1" />
                Stock actual
              </label>
              <div className="flex items-center p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700">
                <input
                  type="checkbox"
                  id="incluirStock"
                  checked={parametros.incluirStock}
                  onChange={(e) =>
                    setParametros((prev) => ({
                      ...prev,
                      incluirStock: e.target.checked,
                    }))
                  }
                  className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
                />
                <label
                  htmlFor="incluirStock"
                  className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300 flex-1"
                >
                  Considerar inventario actual
                </label>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Descuenta el stock disponible
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={handleGenerarSugerencias}
              disabled={cargando}
              className="px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors flex items-center space-x-2 disabled:opacity-50"
            >
              {cargando ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span>Calculando nuevas sugerencias...</span>
                </>
              ) : (
                <>
                  <TrendingUp className="h-4 w-4" />
                  <span>Generar Sugerencias</span>
                </>
              )}
            </button>

            {sugerencias && (
              <button
                onClick={onCrearOrdenes}
                disabled={cargandoOrdenes}
                className="px-4 py-2 bg-green-600 dark:bg-green-700 text-white rounded-lg hover:bg-green-700 dark:hover:bg-green-600 transition-colors flex items-center space-x-2 disabled:opacity-50"
              >
                {cargandoOrdenes ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    <span>Creando...</span>
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4" />
                    <span>Crear Órdenes</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mostrar error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        </div>
      )}

      {/* Lista de sugerencias o placeholder */}
      {sugerencias ? (
        <div className="space-y-4">
          {/* Configuración de Fecha de Entrega */}
          {sugerenciasEditables && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3 flex items-center">
                <Calendar className="h-5 w-5 mr-2" />
                Configurar Órdenes de Compra
              </h3>
              <div className="flex items-center space-x-4">
                <label className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  Fecha de entrega deseada:
                </label>
                <input
                  type="date"
                  value={fechaEntregaDeseada}
                  onChange={(e) => onSetFechaEntrega(e.target.value)}
                  className="px-3 py-2 border border-blue-300 dark:border-blue-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
                <span className="text-sm text-blue-600 dark:text-blue-400">
                  💡 Los precios editados se guardarán como nuevos precios de
                  referencia
                </span>
              </div>
            </div>
          )}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
          {Object.entries(
            (sugerenciasEditables || sugerencias).sugerencias_por_proveedor
          ).map(([proveedor, datos]) => (
            <ProveedorDroppableCard
              key={proveedor}
              proveedor={proveedor}
              esSinProveedor={proveedor === 'Sin proveedor'}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
                      <span className="mr-2">🏪</span>
                      {proveedor}
                    </h3>
                    {proveedor === 'Sin proveedor' && (
                      <span
                        className="text-xs text-amber-700 dark:text-amber-400 italic"
                        title="Estos ingredientes no tienen proveedor habitual asignado en la ficha. Asigna uno con 'Cambiar proveedor' o desde Ingredientes."
                      >
                        (ingredientes sin proveedor habitual asignado)
                      </span>
                    )}

                    {/* Botón cambiar proveedor (solo en modo edición) */}
                    {sugerenciasEditables && (
                      <button
                        onClick={() => {
                          // Buscar el primer ingrediente seleccionado de este proveedor
                          const ingredienteSeleccionado = (
                            datos as any
                          ).ingredientes.find((ing: any) =>
                            ingredientesSeleccionados.has(
                              `${proveedor}-${ing.ingrediente_id}`
                            )
                          );

                          if (ingredienteSeleccionado) {
                            onAbrirModalCambioProveedor(
                              proveedor,
                              ingredienteSeleccionado.ingrediente_id,
                              ingredienteSeleccionado.nombre_ingrediente,
                              ingredienteSeleccionado.categoria
                            );
                          }
                        }}
                        disabled={
                          !(datos as any).ingredientes.some((ing: any) =>
                            ingredientesSeleccionados.has(
                              `${proveedor}-${ing.ingrediente_id}`
                            )
                          )
                        }
                        className="px-3 py-1 text-xs font-medium rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 enabled:hover:border-blue-500 enabled:hover:text-blue-600 dark:enabled:hover:text-blue-400"
                      >
                        Cambiar proveedor
                      </button>
                    )}
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 px-4 py-2 rounded-lg border border-blue-200 dark:border-blue-700">
                      <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                        Total del proveedor:
                      </span>
                      <span className="text-xl font-bold text-blue-900 dark:text-blue-100 ml-2">
                        $
                        {(() => {
                          const datosParaCalcular =
                            sugerenciasEditables?.sugerencias_por_proveedor?.[
                              proveedor
                            ] || datos;
                          return formatearNumero(
                            (datosParaCalcular as any).total_estimado ?? 0
                          );
                        })()}
                      </span>
                    </div>

                    {/* Botón para crear orden de compra de este proveedor O mostrar estado */}
                    {sugerenciasEditables &&
                      (() => {
                        const infoOrden = obtenerOrdenProveedor(proveedor);

                        // Si tiene orden, mostrar estado correspondiente
                        if (infoOrden.tieneOrden && infoOrden.ordenId) {
                          const { estado } = infoOrden;

                          // Estado: Borrador - permite ver/editar
                          if (estado === 'borrador') {
                            return (
                              <button
                                onClick={() =>
                                  onVerDetallesOrden(infoOrden.ordenId!)
                                }
                                className="flex items-center space-x-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors font-medium text-sm"
                              >
                                <FileText className="h-4 w-4" />
                                <span>O.C. en Borrador</span>
                              </button>
                            );
                          }

                          // Estado: Enviada - mostrar badge informativo
                          if (estado === 'enviada') {
                            return (
                              <button
                                onClick={() =>
                                  onVerDetallesOrden(infoOrden.ordenId!)
                                }
                                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium text-sm"
                              >
                                <Send className="h-4 w-4" />
                                <span>O.C. Enviada</span>
                              </button>
                            );
                          }

                          // Estado: Recibida - mostrar badge de éxito
                          if (estado === 'recibida') {
                            return (
                              <button
                                onClick={() =>
                                  onVerDetallesOrden(infoOrden.ordenId!)
                                }
                                className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium text-sm"
                              >
                                <CheckCircle className="h-4 w-4" />
                                <span>O.C. Recibida</span>
                              </button>
                            );
                          }

                          // Estado: Cancelada - mostrar badge de error
                          if (estado === 'cancelada') {
                            return (
                              <button
                                onClick={() =>
                                  onVerDetallesOrden(infoOrden.ordenId!)
                                }
                                className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium text-sm"
                              >
                                <XCircle className="h-4 w-4" />
                                <span>O.C. Cancelada</span>
                              </button>
                            );
                          }
                        }

                        // No tiene orden activa - mostrar botón para crear
                        // Verificar si hay órdenes canceladas para mostrar indicador
                        const ordenesCanceladas = ordenesCompletas.filter(
                          (orden: any) =>
                            orden.proveedor === proveedor &&
                            orden.estado === 'cancelada'
                        );
                        const tieneCanceladas = ordenesCanceladas.length > 0;

                        return (
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => onCrearOrdenProveedor(proveedor)}
                              disabled={
                                cargandoCrearOrdenes ||
                                !(datos as any).ingredientes.some((ing: any) =>
                                  ingredientesSeleccionados.has(
                                    `${proveedor}-${ing.ingrediente_id}`
                                  )
                                )
                              }
                              className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg transition-colors disabled:cursor-not-allowed font-medium text-sm"
                            >
                              {cargandoCrearOrdenes ? (
                                <>
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  <span>Creando...</span>
                                </>
                              ) : (
                                <>
                                  <ShoppingCart className="h-4 w-4" />
                                  <span>Crear Orden</span>
                                </>
                              )}
                            </button>

                            {/* Indicador de órdenes canceladas previas */}
                            {tieneCanceladas && (
                              <button
                                onClick={() =>
                                  onVerDetallesOrden(ordenesCanceladas[0].id)
                                }
                                className="flex items-center space-x-1 px-3 py-2 bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 rounded-lg transition-colors text-xs border border-red-300 dark:border-red-700"
                                title={`${ordenesCanceladas.length} orden(es) cancelada(s) - Click para ver`}
                              >
                                <XCircle className="h-3 w-3" />
                                <span>
                                  {ordenesCanceladas.length} cancelada
                                  {ordenesCanceladas.length > 1 ? 's' : ''}
                                </span>
                              </button>
                            )}
                          </div>
                        );
                      })()}
                  </div>
                </div>

                <div className="space-y-3">
                  {/* Checkbox para seleccionar todos los ingredientes del proveedor */}
                  {sugerenciasEditables && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(datos as any).ingredientes.every(
                            (ing: any) =>
                              ingredientesSeleccionados.has(
                                `${proveedor}-${ing.ingrediente_id}`
                              )
                          )}
                          onChange={(e) => {
                            (datos as any).ingredientes.forEach((ing: any) => {
                              if (
                                e.target.checked !==
                                ingredientesSeleccionados.has(
                                  `${proveedor}-${ing.ingrediente_id}`
                                )
                              ) {
                                onToggleSeleccion(
                                  proveedor,
                                  ing.ingrediente_id
                                );
                              }
                            });
                          }}
                          className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Seleccionar todos los ingredientes
                        </span>
                      </label>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {
                          (datos as any).ingredientes.filter((ing: any) =>
                            ingredientesSeleccionados.has(
                              `${proveedor}-${ing.ingrediente_id}`
                            )
                          ).length
                        }{' '}
                        de {(datos as any).ingredientes.length} seleccionados
                      </span>
                    </div>
                  )}

                  {(datos as any).ingredientes.map((ingrediente: any) => {
                    const isSelected = ingredientesSeleccionados.has(
                      `${proveedor}-${ingrediente.ingrediente_id}`
                    );
                    const datosEditables =
                      sugerenciasEditables?.sugerencias_por_proveedor?.[
                        proveedor
                      ]?.ingredientes?.find(
                        (ing: any) =>
                          ing.ingrediente_id === ingrediente.ingrediente_id
                      );
                    const precioUnitarioEditable =
                      datosEditables?.costo_unitario ??
                      ingrediente.costo_unitario;

                    return (
                      <IngredienteDraggableRow
                        key={ingrediente.ingrediente_id}
                        proveedor={proveedor}
                        ingredienteId={ingrediente.ingrediente_id}
                        disabled={!sugerenciasEditables}
                      >
                      <div
                        className={`flex items-center justify-between py-3 px-4 border border-gray-200 dark:border-gray-600 rounded-lg ${
                          sugerenciasEditables && !isSelected
                            ? 'opacity-50 bg-gray-50 dark:bg-gray-700'
                            : 'bg-white dark:bg-gray-800'
                        }`}
                      >
                        {/* Checkbox de selección individual */}
                        {sugerenciasEditables && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() =>
                              onToggleSeleccion(
                                proveedor,
                                ingrediente.ingrediente_id
                              )
                            }
                            className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        )}

                        <div className="flex-1">
                          <div>
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {ingrediente.nombre_ingrediente}
                            </span>
                            <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                              ({ingrediente.categoria})
                            </span>
                          </div>

                          {/* Chip "En OC pendiente": el backend devuelve
                              ingrediente.oc_emitidas_pendientes con las OCs
                              en estado borrador / enviada / parcial que ya
                              tienen este ingrediente. El chip evita que el
                              usuario pida de nuevo lo que ya está en camino. */}
                          {ingrediente.oc_emitidas_pendientes &&
                            ingrediente.oc_emitidas_pendientes.length > 0 && (
                              <div
                                className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 border border-yellow-300 dark:border-yellow-700"
                                title={`Ya hay ${ingrediente.oc_emitidas_pendientes.length} OC(s) emitidas con este ingrediente`}
                              >
                                <span aria-hidden>🟡</span>
                                <span>
                                  En OC #
                                  {
                                    ingrediente.oc_emitidas_pendientes[0]
                                      .numero_orden
                                  }
                                  {ingrediente.oc_emitidas_pendientes.length >
                                    1 &&
                                    ` +${ingrediente.oc_emitidas_pendientes.length - 1}`}
                                </span>
                              </div>
                            )}

                          {/* Info de stock disponible */}
                          {ingrediente.stock_actual !== undefined && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              <span>
                                Stock: {ingrediente.stock_actual}{' '}
                                {ingrediente.unidad}
                              </span>
                              {ingrediente.cantidad_en_transito > 0 && (
                                <>
                                  <span className="mx-1">+</span>
                                  <span className="text-blue-600 dark:text-blue-400 font-medium">
                                    Tránsito: {ingrediente.cantidad_en_transito}{' '}
                                    {ingrediente.unidad}
                                  </span>
                                  <span className="mx-1">=</span>
                                  <span className="font-medium text-green-600 dark:text-green-400">
                                    Disponible:{' '}
                                    {ingrediente.stock_actual +
                                      ingrediente.cantidad_en_transito}{' '}
                                    {ingrediente.unidad}
                                  </span>
                                </>
                              )}
                              {/* Texto contextual: distingue demanda real del
                                  período (Necesario: N) de reposición pura por
                                  stock bajo mínimo (no había demanda). */}
                              {ingrediente.motivo === 'reposicion' ? (
                                <span className="ml-2 text-amber-600 dark:text-amber-400 font-medium">
                                  ⚠ Reposición — stock {ingrediente.stock_actual}{' '}
                                  ≤ mínimo {ingrediente.stock_minimo ?? '—'}{' '}
                                  {ingrediente.unidad}
                                </span>
                              ) : (
                                ingrediente.cantidad_necesaria !== undefined &&
                                ingrediente.cantidad_necesaria > 0 && (
                                  <span className="ml-2 text-orange-600 dark:text-orange-400">
                                    (Necesario: {ingrediente.cantidad_necesaria}{' '}
                                    {ingrediente.unidad})
                                  </span>
                                )
                              )}
                            </div>
                          )}
                        </div>

                        <div className="flex items-start space-x-6">
                          {/* Campo editable de cantidad */}
                          {sugerenciasEditables ? (
                            <div className="text-right min-w-[140px]">
                              <div className="flex items-center justify-end space-x-1 mb-1">
                                <input
                                  type="text"
                                  value={formatearNumero(
                                    datosEditables?.cantidad_sugerida ??
                                      ingrediente.cantidad_sugerida
                                  )}
                                  onChange={(e) => {
                                    const valor = parsearNumero(e.target.value);
                                    onActualizarCantidad(
                                      proveedor,
                                      ingrediente.ingrediente_id,
                                      valor
                                    );
                                  }}
                                  disabled={!isSelected}
                                  className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50 text-right font-medium"
                                  placeholder="0"
                                />
                                <span className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                  {ingrediente.unidad}
                                </span>
                              </div>
                              {ingrediente.formato_compra_sugerido && (
                                <div className="flex flex-col items-end gap-0.5">
                                  <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                    ≈ {ingrediente.formato_compra_sugerido}
                                  </span>
                                  {ingrediente.cantidad_sugerida_base &&
                                    ingrediente.unidad_base_simbolo && (
                                      <span className="text-[10px] text-gray-500 dark:text-gray-400">
                                        {formatearNumero(
                                          ingrediente.cantidad_sugerida_base
                                        )}{' '}
                                        {ingrediente.unidad_base_simbolo}
                                      </span>
                                    )}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-right min-w-[140px]">
                              <p className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                                {ingrediente.cantidad_sugerida}{' '}
                                {ingrediente.unidad}
                              </p>
                              {ingrediente.formato_compra_sugerido && (
                                <div className="flex flex-col items-end gap-0.5">
                                  <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                    ≈ {ingrediente.formato_compra_sugerido}
                                  </span>
                                  {ingrediente.cantidad_sugerida_base &&
                                    ingrediente.unidad_base_simbolo && (
                                      <span className="text-[10px] text-gray-500 dark:text-gray-400">
                                        {formatearNumero(
                                          ingrediente.cantidad_sugerida_base
                                        )}{' '}
                                        {ingrediente.unidad_base_simbolo}
                                      </span>
                                    )}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Input editable de precio unitario */}
                          {sugerenciasEditables ? (
                            <div className="text-right min-w-[180px]">
                              <div className="flex items-center justify-end space-x-1 mb-1">
                                <span className="text-sm text-gray-500 dark:text-gray-400">
                                  $
                                </span>
                                <input
                                  type="text"
                                  value={formatearNumero(
                                    precioUnitarioEditable || 0
                                  )}
                                  onChange={(e) => {
                                    const valor = parsearNumero(e.target.value);
                                    onActualizarPrecio(
                                      proveedor,
                                      ingrediente.ingrediente_id,
                                      valor
                                    );
                                  }}
                                  disabled={!isSelected}
                                  className="w-24 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50 text-right"
                                  placeholder="0"
                                />
                                <span className="text-xs text-gray-400 whitespace-nowrap">
                                  /{ingrediente.unidad}
                                </span>
                              </div>
                              <p className="text-xs text-gray-400 dark:text-gray-500">
                                Total: $
                                {formatearNumero(
                                  (ingrediente.cantidad_sugerida || 0) *
                                    (precioUnitarioEditable || 0)
                                )}
                              </p>
                            </div>
                          ) : (
                            <div className="text-right min-w-[180px]">
                              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                                $
                                {formatearNumero(
                                  ingrediente.costo_unitario || 0
                                )}
                                /{ingrediente.unidad}
                              </p>
                              <p className="text-xs text-gray-400 dark:text-gray-500">
                                Total: $
                                {formatearNumero(
                                  ingrediente.costo_estimado || 0
                                )}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      </IngredienteDraggableRow>
                    );
                  })}
                </div>
              </div>
            </ProveedorDroppableCard>
          ))}
          <DragOverlay>
            {activeDragId && activeDragId.startsWith('item:') ? (
              <div className="bg-white dark:bg-gray-800 border-2 border-blue-500 rounded-lg px-4 py-3 shadow-lg text-sm font-medium text-gray-900 dark:text-gray-100">
                {(() => {
                  const itemKey = activeDragId.slice('item:'.length);
                  const sepIdx = itemKey.lastIndexOf('|');
                  if (sepIdx < 0) return 'Moviendo ingrediente...';
                  const proveedor = itemKey.slice(0, sepIdx);
                  const ingId = parseInt(itemKey.slice(sepIdx + 1), 10);
                  const datos =
                    sugerenciasEditables?.sugerencias_por_proveedor?.[
                      proveedor
                    ];
                  const ing = datos?.ingredientes?.find(
                    (i: any) => i.ingrediente_id === ingId
                  );
                  return ing?.nombre_ingrediente || 'Moviendo ingrediente...';
                })()}
              </div>
            ) : null}
          </DragOverlay>
          </DndContext>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="p-6 text-center">
            <TrendingUp className="h-12 w-12 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              No hay sugerencias generadas
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Configura los parámetros y haz clic en "Generar Sugerencias" para
              obtener recomendaciones de compra
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Componente para Órdenes de Compra
interface OrdenesContentProps {
  ordenes: any[];
  cargando: boolean;
  error: string | null;
  filtros: {
    estado: string;
    proveedor: string;
    busqueda: string;
  };
  onSetFiltros: (filtros: any) => void;

  // Props de paginación
  paginaActual: number;
  elementosPorPagina: number;
  onCambiarPagina: (pagina: number) => void;

  onCrearOrden: () => void;
  onVerDetalles: (ordenId: string) => void;
  onEditarOrden: (ordenId: string) => void;
  onRecepcionar: (ordenId: string) => void;
  onCambiarEstado: (
    ordenId: string,
    nuevoEstado: string,
    motivo?: string
  ) => Promise<void>;
  formatearMoneda: (cantidad: number) => string;
  estadoColor: (estado: string) => string;
  estadoIcon: (estado: string) => JSX.Element;
}

const OrdenesContent: React.FC<OrdenesContentProps> = ({
  ordenes,
  cargando,
  error,
  filtros,
  onSetFiltros,
  paginaActual,
  elementosPorPagina,
  onCambiarPagina,
  onCrearOrden,
  onVerDetalles,
  onEditarOrden,
  onRecepcionar,
  onCambiarEstado,
  formatearMoneda,
  estadoColor,
  estadoIcon,
}) => {
  // Filtrar órdenes según los filtros aplicados
  const ordenesFiltradas = ordenes.filter((orden) => {
    if (filtros.estado && orden.estado !== filtros.estado) return false;
    if (filtros.busqueda) {
      const proveedor =
        typeof orden.proveedor === 'string'
          ? orden.proveedor
          : orden.proveedor.nombre;
      const cumpleBusqueda =
        proveedor.toLowerCase().includes(filtros.busqueda.toLowerCase()) ||
        orden.numero.toLowerCase().includes(filtros.busqueda.toLowerCase());
      if (!cumpleBusqueda) return false;
    }
    return true;
  });

  // Calcular paginación
  const totalPaginas = Math.ceil(ordenesFiltradas.length / elementosPorPagina);
  const indiceInicio = (paginaActual - 1) * elementosPorPagina;
  const indiceFin = indiceInicio + elementosPorPagina;
  const ordenesPaginadas = ordenesFiltradas.slice(indiceInicio, indiceFin);

  // Calcular métricas
  const metricas = {
    activas: ordenes.filter((o) =>
      ['borrador', 'enviada', 'parcial'].includes(o.estado)
    ).length,
    enTransito: ordenes.filter((o) => o.estado === 'enviada').length,
    paraRecibir: ordenes.filter((o) =>
      ['enviada', 'parcial'].includes(o.estado)
    ).length,
    completadasHoy: ordenes.filter((o) => {
      const hoy = new Date().toDateString();
      return (
        o.estado === 'recibida' &&
        new Date(o.fechaRecepcion || 0).toDateString() === hoy
      );
    }).length,
  };

  if (cargando) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Métricas de órdenes */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Órdenes Activas
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {metricas.activas}
              </p>
            </div>
            <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
              <Clock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                En Tránsito
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {metricas.enTransito}
              </p>
            </div>
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Truck className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Para Recibir
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {metricas.paraRecibir}
              </p>
            </div>
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Package className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Completadas Hoy
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {metricas.completadasHoy}
              </p>
            </div>
            <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
              <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Barra de acciones y filtros */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onCrearOrden}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Nueva Orden
            </button>

            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Buscar por proveedor o número..."
                  value={filtros.busqueda}
                  onChange={(e) =>
                    onSetFiltros({
                      ...filtros,
                      busqueda: e.target.value,
                    })
                  }
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <select
              value={filtros.estado}
              onChange={(e) =>
                onSetFiltros({
                  ...filtros,
                  estado: e.target.value,
                })
              }
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="">Todos los estados</option>
              <option value="borrador">Borrador</option>
              <option value="enviada">Enviada</option>
              <option value="recibida">Recibida</option>
              <option value="cancelada">Cancelada</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tabla de órdenes */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        {ordenesFiltradas.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              No hay órdenes de compra
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {filtros.estado || filtros.busqueda
                ? 'No se encontraron órdenes con los filtros aplicados'
                : 'Comienza creando tu primera orden de compra'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Número de Orden
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Proveedor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Fecha
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Items
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {ordenesPaginadas.map((orden: OrdenCompra) => (
                    <tr
                      key={orden.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {orden.numero}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {typeof orden.proveedor === 'string'
                            ? orden.proveedor
                            : orden.proveedor.nombre}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${estadoColor(orden.estado)}`}
                        >
                          {estadoIcon(orden.estado)}
                          <span className="ml-1 capitalize">
                            {orden.estado}
                          </span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {formatearFechaCorta(orden.fecha)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {formatearMoneda(orden.total || 0)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {orden.items?.length || 0} items
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => onVerDetalles(orden.id)}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:bg-gray-800 dark:hover:bg-gray-700"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            Ver
                          </button>

                          {orden.estado === 'borrador' && (
                            <button
                              onClick={() => onEditarOrden(orden.id)}
                              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:bg-gray-800 dark:hover:bg-gray-700"
                            >
                              <Edit className="h-3 w-3 mr-1" />
                              Editar
                            </button>
                          )}

                          {(orden.estado === 'enviada' ||
                            orden.estado === 'parcial') && (
                            <button
                              onClick={() => onRecepcionar(orden.id)}
                              className="inline-flex items-center px-3 py-1.5 border border-green-300 shadow-sm text-xs font-medium rounded text-green-700 bg-green-50 hover:bg-green-100 dark:border-green-600 dark:text-green-300 dark:bg-green-900/20 dark:hover:bg-green-900/30"
                            >
                              <Package className="h-3 w-3 mr-1" />
                              Recepcionar
                            </button>
                          )}

                          {orden.estado === 'borrador' && (
                            <button
                              onClick={() =>
                                onCambiarEstado(orden.id, 'enviada')
                              }
                              className="inline-flex items-center px-3 py-1.5 border border-blue-300 shadow-sm text-xs font-medium rounded text-blue-700 bg-blue-50 hover:bg-blue-100 dark:border-blue-600 dark:text-blue-300 dark:bg-blue-900/20 dark:hover:bg-blue-900/30"
                            >
                              <Send className="h-3 w-3 mr-1" />
                              Enviar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            {totalPaginas > 1 && (
              <div className="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() =>
                        onCambiarPagina(Math.max(1, paginaActual - 1))
                      }
                      disabled={paginaActual === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Anterior
                    </button>
                    <button
                      onClick={() =>
                        onCambiarPagina(
                          Math.min(totalPaginas, paginaActual + 1)
                        )
                      }
                      disabled={paginaActual === totalPaginas}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Siguiente
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        Mostrando{' '}
                        <span className="font-medium">{indiceInicio + 1}</span>{' '}
                        a{' '}
                        <span className="font-medium">
                          {Math.min(indiceFin, ordenesFiltradas.length)}
                        </span>{' '}
                        de{' '}
                        <span className="font-medium">
                          {ordenesFiltradas.length}
                        </span>{' '}
                        resultados
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() =>
                            onCambiarPagina(Math.max(1, paginaActual - 1))
                          }
                          disabled={paginaActual === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Anterior
                        </button>

                        {Array.from(
                          { length: totalPaginas },
                          (_, i) => i + 1
                        ).map((numero) => (
                          <button
                            key={numero}
                            onClick={() => onCambiarPagina(numero)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              numero === paginaActual
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {numero}
                          </button>
                        ))}

                        <button
                          onClick={() =>
                            onCambiarPagina(
                              Math.min(totalPaginas, paginaActual + 1)
                            )
                          }
                          disabled={paginaActual === totalPaginas}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Siguiente
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Componente para Historial
interface HistorialContentProps {
  historial: any[];
  cargando: boolean;
  error: string | null;
  onRecargar: () => Promise<void>;
  onVerDetalles: (ordenId: string) => void;
}

const HistorialContent: React.FC<HistorialContentProps> = ({
  historial,
  cargando,
  error,
  onRecargar: _onRecargar,
  onVerDetalles,
}) => {
  const [filtroProveedor, setFiltroProveedor] = useState('');
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('');
  const [paginaActual, setPaginaActual] = useState(1);
  const elementosPorPagina = 15;

  // Filtrar historial
  const historialFiltrado = historial.filter((orden) => {
    const cumpleFiltroProveedor =
      !filtroProveedor ||
      orden.proveedor.toLowerCase().includes(filtroProveedor.toLowerCase());

    const cumpleFiltroFecha =
      (!filtroFechaDesde ||
        new Date(orden.fechaRecepcion || orden.fecha) >=
          new Date(filtroFechaDesde)) &&
      (!filtroFechaHasta ||
        new Date(orden.fechaRecepcion || orden.fecha) <=
          new Date(filtroFechaHasta));

    return cumpleFiltroProveedor && cumpleFiltroFecha;
  });

  // Paginación
  const indiceInicio = (paginaActual - 1) * elementosPorPagina;
  const indiceFin = indiceInicio + elementosPorPagina;
  const historialPaginado = historialFiltrado.slice(indiceInicio, indiceFin);
  const totalPaginas = Math.ceil(historialFiltrado.length / elementosPorPagina);

  const formatearFecha = (fecha: string | Date) => {
    return formatearFechaCon(fecha, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatearMoneda = (valor: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
    }).format(valor);
  };

  if (cargando) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
        <p className="text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Proveedor
            </label>
            <input
              type="text"
              value={filtroProveedor}
              onChange={(e) => {
                setFiltroProveedor(e.target.value);
                setPaginaActual(1);
              }}
              placeholder="Buscar proveedor..."
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Desde
            </label>
            <input
              type="date"
              value={filtroFechaDesde}
              onChange={(e) => {
                setFiltroFechaDesde(e.target.value);
                setPaginaActual(1);
              }}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Hasta
            </label>
            <input
              type="date"
              value={filtroFechaHasta}
              onChange={(e) => {
                setFiltroFechaHasta(e.target.value);
                setPaginaActual(1);
              }}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>

        {/* Resultados */}
        <div className="mt-4 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          <span>
            {historialFiltrado.length} orden
            {historialFiltrado.length !== 1 && 'es'} recibida
            {historialFiltrado.length !== 1 && 's'}
          </span>
          {(filtroProveedor || filtroFechaDesde || filtroFechaHasta) && (
            <button
              onClick={() => {
                setFiltroProveedor('');
                setFiltroFechaDesde('');
                setFiltroFechaHasta('');
                setPaginaActual(1);
              }}
              className="text-blue-600 dark:text-blue-400 hover:underline"
            >
              Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {/* Tabla de historial */}
      {historialFiltrado.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="p-6 text-center">
            <Calendar className="h-12 w-12 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              {filtroProveedor || filtroFechaDesde || filtroFechaHasta
                ? 'No se encontraron órdenes'
                : 'Historial vacío'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {filtroProveedor || filtroFechaDesde || filtroFechaHasta
                ? 'Intenta ajustar los filtros'
                : 'Las órdenes recibidas aparecerán aquí'}
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Número de Orden
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Proveedor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Fecha Orden
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Fecha Recepción
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Items
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {historialPaginado.map((orden: OrdenCompra) => (
                    <tr
                      key={orden.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Package className="h-4 w-4 text-gray-400 dark:text-gray-600 mr-2" />
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {orden.numero}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900 dark:text-gray-100">
                          {typeof orden.proveedor === 'string'
                            ? orden.proveedor
                            : orden.proveedor?.nombre || ''}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {formatearFecha(orden.fecha)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-green-600 dark:text-green-400">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          {formatearFecha(orden.fechaRecepcion || orden.fecha)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {orden.items?.length || 0} item
                          {(orden.items?.length || 0) !== 1 && 's'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {formatearMoneda(orden.total || 0)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => onVerDetalles(orden.id)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors flex items-center space-x-1"
                          title="Ver detalles"
                        >
                          <Eye className="h-4 w-4" />
                          <span className="text-sm">Ver</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Paginación */}
          {totalPaginas > 1 && (
            <div className="bg-white dark:bg-gray-800 px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Mostrando {indiceInicio + 1} a{' '}
                    {Math.min(indiceFin, historialFiltrado.length)} de{' '}
                    {historialFiltrado.length} resultados
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() =>
                      setPaginaActual(Math.max(1, paginaActual - 1))
                    }
                    disabled={paginaActual === 1}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Página {paginaActual} de {totalPaginas}
                  </span>
                  <button
                    onClick={() =>
                      setPaginaActual(Math.min(totalPaginas, paginaActual + 1))
                    }
                    disabled={paginaActual === totalPaginas}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Compras;

// Componente Modal de Cambio de Proveedor
interface ModalCambioProveedorProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmar: (nuevoProveedorId: number) => void;
  proveedorActual: string;
  nombreIngrediente: string;
  // Categoría del ingrediente (ej: "CARNES", "VERDURAS"). Cuando se pasa,
  // se usa para filtrar los proveedores sugeridos por categoría
  // correspondiente. Sin ella, fallback al matching por keyword del nombre.
  categoriaIngrediente?: string;
  proveedores: any[];
}

const ModalCambioProveedor: React.FC<ModalCambioProveedorProps> = ({
  isOpen,
  onClose,
  onConfirmar,
  proveedorActual,
  nombreIngrediente,
  categoriaIngrediente,
  proveedores,
}) => {
  const [busqueda, setBusqueda] = useState('');
  // Guardamos el ID del proveedor (number). El backend lo necesita como
  // proveedor_id, y el ID también es estable (a diferencia del nombre que
  // puede cambiar/normalizarse).
  const [proveedorSeleccionado, setProveedorSeleccionado] = useState<
    number | null
  >(null);
  const [cargando, setCargando] = useState(false);

  // Resetear estado cuando se abre/cierra el modal
  useEffect(() => {
    if (isOpen) {
      setBusqueda('');
      setProveedorSeleccionado(null);
      setCargando(false);
    }
  }, [isOpen]);

  // Mapping fijo: categoría del ingrediente (catálogo `categorias_generales`
  // tipo='ingrediente') → categorías de proveedor relevantes (ENUM en
  // proveedores.categoria). Cuando viene del backend con la categoría real,
  // este mapping decide qué proveedores se muestran como "sugeridos".
  const mapearCategoriaIngredienteAProveedor = (
    categoriaIng: string
  ): string[] => {
    const c = categoriaIng.toUpperCase();
    if (c === 'VERDURAS' || c === 'FRUTAS') return ['verduleria'];
    if (c === 'CARNES' || c === 'PROTEINAS' || c === 'PROTEÍNAS')
      return ['carniceria', 'mariscos'];
    if (c === 'LACTEOS' || c === 'LÁCTEOS') return ['lacteos'];
    if (
      c === 'GRANOS' ||
      c === 'CONDIMENTOS' ||
      c === 'ACEITES' ||
      c === 'ABARROTES' ||
      c === 'OTROS'
    )
      return ['supermercado'];
    return [];
  };

  // Fallback heurístico: si no hay categoría del ingrediente, intenta
  // adivinar por keywords en el nombre. Menos fiable, pero útil para datos
  // que no tienen categoría asignada.
  const adivinarCategoriasPorNombre = (nombreIngrediente: string): string[] => {
    const nombre = nombreIngrediente.toLowerCase();

    if (
      nombre.includes('carne') ||
      nombre.includes('pollo') ||
      nombre.includes('cerdo') ||
      nombre.includes('vacuno') ||
      nombre.includes('cordero') ||
      nombre.includes('pavo') ||
      nombre.includes('bistec') ||
      nombre.includes('chuleta') ||
      nombre.includes('filete') ||
      nombre.includes('jamón') ||
      nombre.includes('jamon')
    ) {
      return ['carniceria'];
    }

    if (
      nombre.includes('pescado') ||
      nombre.includes('marisco') ||
      nombre.includes('camarón') ||
      nombre.includes('caracol') ||
      nombre.includes('atún') ||
      nombre.includes('atun') ||
      nombre.includes('salmón') ||
      nombre.includes('salmon')
    ) {
      return ['mariscos'];
    }

    if (
      nombre.includes('verdura') ||
      nombre.includes('fruta') ||
      nombre.includes('lechuga') ||
      nombre.includes('tomate') ||
      nombre.includes('cebolla') ||
      nombre.includes('zanahoria') ||
      nombre.includes('papa') ||
      nombre.includes('apio') ||
      nombre.includes('pimiento') ||
      nombre.includes('pepino') ||
      nombre.includes('limón') ||
      nombre.includes('naranja') ||
      nombre.includes('plátano') ||
      nombre.includes('platano') ||
      nombre.includes('yuca') ||
      nombre.includes('chile')
    ) {
      return ['verduleria'];
    }

    if (
      nombre.includes('pan') ||
      nombre.includes('masa') ||
      nombre.includes('harina') ||
      nombre.includes('levadura') ||
      nombre.includes('bollito') ||
      nombre.includes('marraqueta')
    ) {
      return ['panaderia'];
    }

    if (
      nombre.includes('leche') ||
      nombre.includes('queso') ||
      nombre.includes('yogur') ||
      nombre.includes('mantequilla') ||
      nombre.includes('crema') ||
      nombre.includes('nata')
    ) {
      return ['lacteos'];
    }

    return [];
  };

  // Resolver categorías relevantes: prioriza la categoría real del
  // ingrediente; si no hay (o el mapping no la cubre), cae al heurístico
  // por nombre. Si ambos fallan, devuelve [] y el modal muestra todos los
  // proveedores como "Otros" (sin grupo de sugeridos).
  const obtenerCategoriasRelevantes = (): string[] => {
    if (categoriaIngrediente) {
      const desdeCategoria =
        mapearCategoriaIngredienteAProveedor(categoriaIngrediente);
      if (desdeCategoria.length > 0) return desdeCategoria;
    }
    return adivinarCategoriasPorNombre(nombreIngrediente);
  };

  // Filtrar proveedores basado en la búsqueda y relevancia por categoría
  const categoriasRelevantes = obtenerCategoriasRelevantes();

  const proveedoresFiltrados = proveedores
    .filter((p) => p.activo && p.nombre !== proveedorActual)
    .sort((a, b) => {
      // Primero ordenar por relevancia de categoría
      const aEsRelevante = categoriasRelevantes.includes(a.categoria);
      const bEsRelevante = categoriasRelevantes.includes(b.categoria);

      if (aEsRelevante && !bEsRelevante) return -1;
      if (!aEsRelevante && bEsRelevante) return 1;

      // Luego ordenar alfabéticamente
      return a.nombre.localeCompare(b.nombre);
    })
    .filter(
      (p) =>
        p.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
        p.categoria.toLowerCase().includes(busqueda.toLowerCase()) ||
        p.rut.includes(busqueda)
    );

  const handleConfirmar = async () => {
    console.log(
      'handleConfirmar called, proveedorSeleccionado:',
      proveedorSeleccionado
    );
    if (proveedorSeleccionado && !cargando) {
      setCargando(true);
      try {
        await onConfirmar(proveedorSeleccionado);
      } finally {
        setCargando(false);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Cambiar Proveedor
            </h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <XCircle className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <div className="mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Ingrediente:{' '}
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {nombreIngrediente}
              </span>
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Proveedor actual:{' '}
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {proveedorActual}
              </span>
            </p>
          </div>

          {/* Buscador */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar proveedores..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Lista de proveedores */}
          <div className="max-h-80 overflow-y-auto space-y-3">
            {proveedoresFiltrados.length === 0 ? (
              <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                No se encontraron proveedores
              </p>
            ) : (
              <div>
                {/* Proveedores sugeridos */}
                {(() => {
                  const sugeridos = proveedoresFiltrados.filter((p) =>
                    categoriasRelevantes.includes(p.categoria)
                  );
                  const otros = proveedoresFiltrados.filter(
                    (p) => !categoriasRelevantes.includes(p.categoria)
                  );

                  return (
                    <>
                      {sugeridos.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 px-1">
                            📍 Proveedores Sugeridos
                          </h4>
                          {sugeridos.map((proveedor) => (
                            <label
                              key={proveedor.id}
                              className={`block p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                                proveedorSeleccionado === proveedor.id
                                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-md'
                                  : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-500'
                              }`}
                            >
                              <input
                                type="radio"
                                name="proveedor"
                                value={proveedor.id}
                                checked={
                                  proveedorSeleccionado === proveedor.id
                                }
                                onChange={() => {
                                  console.log(
                                    'Proveedor seleccionado id:',
                                    proveedor.id
                                  );
                                  setProveedorSeleccionado(proveedor.id);
                                }}
                                className="sr-only"
                              />
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center space-x-2 mb-1">
                                    <p className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                                      {proveedor.nombre}
                                    </p>
                                    <span
                                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                        proveedor.categoria === 'carniceria'
                                          ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                          : proveedor.categoria === 'verduleria'
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                            : proveedor.categoria ===
                                                'panaderia'
                                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                              : proveedor.categoria ===
                                                  'lacteos'
                                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                                                : proveedor.categoria ===
                                                    'supermercado'
                                                  ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
                                                  : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
                                      }`}
                                    >
                                      {proveedor.categoria === 'carniceria'
                                        ? 'Carnicería'
                                        : proveedor.categoria === 'verduleria'
                                          ? 'Verdulería'
                                          : proveedor.categoria === 'panaderia'
                                            ? 'Panadería'
                                            : proveedor.categoria === 'lacteos'
                                              ? 'Lácteos'
                                              : proveedor.categoria ===
                                                  'supermercado'
                                                ? 'Supermercado'
                                                : 'Otros'}
                                    </span>
                                  </div>
                                  <div className="space-y-1">
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                      RUT: {proveedor.rut}
                                    </p>
                                    {proveedor.telefono && (
                                      <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center">
                                        <Phone className="h-3 w-3 mr-1" />
                                        {proveedor.telefono}
                                      </p>
                                    )}
                                    {proveedor.direccion && (
                                      <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center">
                                        <MapPin className="h-3 w-3 mr-1" />
                                        {proveedor.direccion}
                                      </p>
                                    )}
                                    {proveedor.email && (
                                      <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center">
                                        <Mail className="h-3 w-3 mr-1" />
                                        {proveedor.email}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                {proveedorSeleccionado === proveedor.id && (
                                  <CheckCircle className="h-6 w-6 text-blue-600 dark:text-blue-400 flex-shrink-0 ml-3" />
                                )}
                              </div>
                            </label>
                          ))}
                        </div>
                      )}

                      {/* Otros proveedores */}
                      {otros.length > 0 && (
                        <div className="space-y-2">
                          {sugeridos.length > 0 && (
                            <div className="border-t border-gray-200 dark:border-gray-600 my-3"></div>
                          )}
                          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 px-1">
                            🏪 Otros Proveedores
                          </h4>
                          {otros.map((proveedor) => (
                            <label
                              key={proveedor.id}
                              className={`block p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                                proveedorSeleccionado === proveedor.id
                                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-md'
                                  : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-500'
                              }`}
                            >
                              <input
                                type="radio"
                                name="proveedor"
                                value={proveedor.id}
                                checked={
                                  proveedorSeleccionado === proveedor.id
                                }
                                onChange={() => {
                                  console.log(
                                    'Proveedor seleccionado id:',
                                    proveedor.id
                                  );
                                  setProveedorSeleccionado(proveedor.id);
                                }}
                                className="sr-only"
                              />
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center space-x-2 mb-1">
                                    <p className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                                      {proveedor.nombre}
                                    </p>
                                    <span
                                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                        proveedor.categoria === 'carniceria'
                                          ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                          : proveedor.categoria === 'verduleria'
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                            : proveedor.categoria ===
                                                'panaderia'
                                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                              : proveedor.categoria ===
                                                  'lacteos'
                                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                                                : proveedor.categoria ===
                                                    'supermercado'
                                                  ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
                                                  : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
                                      }`}
                                    >
                                      {proveedor.categoria === 'carniceria'
                                        ? 'Carnicería'
                                        : proveedor.categoria === 'verduleria'
                                          ? 'Verdulería'
                                          : proveedor.categoria === 'panaderia'
                                            ? 'Panadería'
                                            : proveedor.categoria === 'lacteos'
                                              ? 'Lácteos'
                                              : proveedor.categoria ===
                                                  'supermercado'
                                                ? 'Supermercado'
                                                : 'Otros'}
                                    </span>
                                  </div>
                                  <div className="space-y-1">
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                      RUT: {proveedor.rut}
                                    </p>
                                    {proveedor.telefono && (
                                      <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center">
                                        <Phone className="h-3 w-3 mr-1" />
                                        {proveedor.telefono}
                                      </p>
                                    )}
                                    {proveedor.direccion && (
                                      <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center">
                                        <MapPin className="h-3 w-3 mr-1" />
                                        {proveedor.direccion}
                                      </p>
                                    )}
                                    {proveedor.email && (
                                      <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center">
                                        <Mail className="h-3 w-3 mr-1" />
                                        {proveedor.email}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                {proveedorSeleccionado === proveedor.id && (
                                  <CheckCircle className="h-6 w-6 text-blue-600 dark:text-blue-400 flex-shrink-0 ml-3" />
                                )}
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={cargando}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirmar}
            disabled={!proveedorSeleccionado || cargando}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center space-x-2"
          >
            {cargando && <Loader2 className="h-4 w-4 animate-spin" />}
            <span>{cargando ? 'Cambiando...' : 'Cambiar Proveedor'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Componentes auxiliares para drag & drop entre tarjetas de proveedor.
//
// Se mantienen co-localizados con el resto del módulo de Compras hasta que
// haya más de un consumidor; si en el futuro otra vista necesita el mismo
// patrón, se mueven a `components/compras/` y se documentan ahí.
// ============================================================================

interface ProveedorDroppableCardProps {
  proveedor: string;
  esSinProveedor: boolean;
  children: React.ReactNode;
}

const ProveedorDroppableCard: React.FC<ProveedorDroppableCardProps> = ({
  proveedor,
  esSinProveedor,
  children,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `provider:${proveedor}`,
    data: { proveedor },
  });
  // Borde dashed amarillo para "Sin proveedor"; ring azul cuando hay un
  // ingrediente siendo arrastrado encima de la card.
  const baseClasses = esSinProveedor
    ? 'bg-amber-50 dark:bg-amber-900/10 rounded-lg border-2 border-dashed border-amber-400 dark:border-amber-700'
    : 'bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700';
  const overClasses = isOver
    ? 'ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-gray-900 transition-shadow'
    : '';
  return (
    <div ref={setNodeRef} className={`${baseClasses} ${overClasses}`}>
      {children}
    </div>
  );
};

interface IngredienteDraggableRowProps {
  proveedor: string;
  ingredienteId: number;
  disabled?: boolean;
  children: React.ReactNode;
}

const IngredienteDraggableRow: React.FC<IngredienteDraggableRowProps> = ({
  proveedor,
  ingredienteId,
  disabled,
  children,
}) => {
  const { setNodeRef, attributes, listeners, isDragging } = useDraggable({
    id: `item:${proveedor}|${ingredienteId}`,
    data: { proveedor, ingredienteId },
    disabled,
  });
  return (
    <div
      ref={setNodeRef}
      className={`relative group ${isDragging ? 'opacity-30' : ''}`}
      {...attributes}
    >
      {!disabled && (
        <button
          type="button"
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-1 text-gray-400 opacity-50 group-hover:opacity-100 hover:text-gray-700 dark:hover:text-gray-200 cursor-grab active:cursor-grabbing transition-opacity"
          aria-label="Arrastrar para mover de proveedor"
          title="Arrastrar a otro proveedor"
          {...listeners}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="4" cy="3" r="1.2" fill="currentColor" />
            <circle cx="4" cy="7" r="1.2" fill="currentColor" />
            <circle cx="4" cy="11" r="1.2" fill="currentColor" />
            <circle cx="10" cy="3" r="1.2" fill="currentColor" />
            <circle cx="10" cy="7" r="1.2" fill="currentColor" />
            <circle cx="10" cy="11" r="1.2" fill="currentColor" />
          </svg>
        </button>
      )}
      <div className={disabled ? '' : 'pl-7'}>{children}</div>
    </div>
  );
};
